/**
 * Unified trilingual string table for the klapzo.app app surface (constitution
 * #6: pt-BR / en-US / es-LATAM from V1). Merges the former live-web + kit-web
 * tables plus login/topbar keys. Marketing copy lives in marketing/Landing.tsx
 * (its own COPY object). Locale resolves from ?lang, then localStorage, then the
 * browser, defaulting to pt-BR.
 */
export type Locale = "pt-BR" | "en-US" | "es-LATAM";
export const LOCALES: Locale[] = ["pt-BR", "en-US", "es-LATAM"];

type Dict = Record<string, string>;

/** Exported so a test can assert every key exists in every locale — a missing one falls back
 *  silently and the page renders in the wrong language with no error anywhere. */
export const STRINGS: Record<Locale, Dict> = {
  "pt-BR": {
    // results dashboard (durable per-round metrics)
    "results.title": "Resultados da sessão",
    "results.back": "Voltar",
    "results.loading": "Carregando resultados…",
    "results.empty": "Nenhuma rodada encerrada ainda. Os resultados aparecem aqui assim que você fechar a primeira pergunta.",
    "results.unavailable": "Não foi possível carregar os resultados agora. Tente novamente em instantes.",
    "results.notFound": "Sessão não encontrada.",
    "results.rounds": "rodadas",
    "results.totalResponses": "respostas no total",
    "results.peakResponses": "maior participação numa rodada",
    "results.peakHint": "Maior número de respostas em uma única rodada — não é a contagem de pessoas na sala.",
    "results.responses": "respostas",
    "results.responseOne": "resposta",
    "results.scored": "Valendo pontos",
    "results.round": "Rodada",
    "results.export": "Baixar CSV",
    "results.exportPdf": "Baixar relatório em PDF",
    "results.exportLocked": "Exportação disponível nos planos pagos",
    "results.exportLockedHint": "Este resultado foi gerado num plano gratuito. A exportação continua bloqueada mesmo depois de você assinar — quem participou desta sala foi informado disso.",
    "results.withheld": "{n} rodada(s) fora do período de retenção do seu plano ({days} dias).",
    "results.withheldHint": "Planos pagos guardam por mais tempo.",
    "results.anonymous": "Autoria oculta",
    "results.report": "Imprimir esta página",
    "results.reportHeader": "Klapzo · Relatório da sessão {id} · gerado em {date}",
    // planos + upgrade (o destino de todo 402)
    "plan.free": "Gratuito",
    "plan.facilitador": "Facilitador",
    "plan.equipe": "Equipe",
    "plan.evento": "Evento",
    "plan.corporativo": "Corporativo",
    "upgrade.title": "Planos Klapzo",
    "upgrade.loading": "Carregando planos…",
    "upgrade.unavailable": "Não foi possível carregar os planos agora. Tente novamente em instantes.",
    "upgrade.back": "Voltar",
    "upgrade.current": "Plano atual",
    "upgrade.currentPlan": "Seu plano",
    "upgrade.buyMonthly": "Assinar por {money}/mês",
    "upgrade.buyAnnual": "Assinar por {money}/ano",
    "upgrade.buyOneOff": "Comprar por {money} — {days} dias",
    "upgrade.redirecting": "Redirecionando…",
    "upgrade.emailPrompt": "E-mail para a cobrança:",
    "upgrade.emailInvalid": "Informe um e-mail válido para continuar.",
    "upgrade.checkoutFailed": "Não foi possível iniciar o pagamento. Tente novamente em instantes.",
    "upgrade.pastDue": "Pagamento pendente.",
    "upgrade.pastDueHint": "Sua assinatura {plan} está com pagamento em atraso, então as salas voltaram aos limites do plano gratuito. Regularize para restaurar o acesso.",
    "upgrade.passActive": "Passe {plan} ativo até {date}.",
    "upgrade.talkToSales": "Falar com vendas",
    "upgrade.taxNote": "Preços em reais, pagáveis por PIX, boleto ou cartão via Mercado Pago.",
    "upgrade.corporativoBlurb": "A partir de R$ 890/mês. Salas de até 1.000 pessoas, 25 assentos, retenção indefinida, SSO e marca própria.",
    "upgrade.blurb.facilitador": "Rodadas ilimitadas, até 100 participantes por sala, exportação CSV/JSON e o Klapzo Kit completo.",
    "upgrade.blurb.equipe": "Tudo do Facilitador, com 5 assentos, 5 salas simultâneas e 200 participantes por sala.",
    "upgrade.blurb.evento": "Passe avulso de 7 dias para uma conferência: 300 participantes por sala, sem assinatura.",
    "limit.roundsTitle": "Limite de rodadas atingido",
    "limit.roundsBody": "O plano {plan} permite {limit} rodadas por sala. Faça upgrade para abrir rodadas ilimitadas.",
    "limit.sessionsTitle": "Limite de salas simultâneas atingido",
    "limit.sessionsBody": "O plano {plan} permite {limit} sala(s) ao mesmo tempo. Encerre uma sala ou faça upgrade.",
    // W11 (US-LV-19): a sessão de longa duração e as janelas que ela recusa.
    "limit.asyncTitle": "Sessão de longa duração não disponível",
    "limit.asyncBody": "Sessões de longa duração estão disponíveis do plano Facilitador para cima. No seu plano uma sala dura {limit} horas.",
    "limit.cta": "Ver planos",
    "sessionOver.title": "Esta sala foi encerrada",
    "sessionOver.closed": "Você encerrou esta sala. Crie uma nova para continuar.",
    "sessionOver.expired": "Uma sala dura no máximo 12 horas e esta chegou ao limite. Crie uma nova para continuar — os resultados desta continuam disponíveis.",
    "results.votes": "votos",

    // promptvote (US-LV-10)
    "pv.title": "Rodada de respostas",
    "pv.write": "Escreva sua resposta",
    "pv.writeHint": "Uma linha só. Sua resposta vai aparecer na tela para o grupo — sem dados pessoais.",
    "pv.sent": "Resposta enviada! Aguarde a votação.",
    "pv.waitingWrite": "O grupo está escrevendo…",
    "pv.submissions": "respostas recebidas",
    "pv.submissionOne": "resposta recebida",
    "pv.vote": "Vote na melhor",
    "pv.voteHint": "Você não pode votar na sua própria resposta.",
    "pv.voted": "Voto registrado!",
    "pv.votesCast": "votos registrados",
    "pv.reveal": "Resultado",
    "pv.votes": "votos",
    "pv.voteOne": "voto",
    // urna honesta (W10a): quantas frases o sorteio da urna não coube. Quem escreveu e não se viu
    // na tela aprende por quê, em vez de supor que foi rejeitado.
    "pv.omitted": "{n} frases ficaram de fora do sorteio da urna.",
    "pv.omittedOne": "1 frase ficou de fora do sorteio da urna.",
    // entrar pela tela (W10a)
    "join.instruction": "Aponte a câmera ou digite o código:",
    "join.qrLabel": "QR code para entrar na sala com o código {code}",
    "projection.more": "+{n} não cabem na tela",
    "projection.moreOne": "+1 não cabe na tela",
    // picker / "Sorteio" (US-LV-6, W5). O nome do produto é Sorteio — nunca "Spinner Wheel".
    "picker.title": "Sorteio",
    "picker.open": "Sorteio",
    "picker.one": "Sortear uma pessoa",
    "picker.weighted": "Sortear quem participou menos",
    "picker.teams": "Sortear times",
    "picker.teamCount": "Nº de times",
    "picker.rotation": "Ordem de rodízio",
    "picker.pool": "ou sorteie desta lista (um por linha)",
    "picker.poolPlaceholder": "Um nome por linha",
    "picker.spinHint": "O sorteio é feito no servidor quando a rodada abre. Ninguém — nem o facilitador — vê o resultado antes de a rodada fechar.",
    "picker.spinning": "Sorteando…",
    "picker.drawn": "Sorteado",
    "picker.nobody": "Ninguém para sortear.",
    "picker.you": "Foi você!",
    "picker.yourTeam": "Seu time: {n}",
    "picker.yourPosition": "Você é o nº {n}",
    "picker.team": "Time",
    "picker.candidates": "nomes no sorteio",
    "picker.candidateOne": "nome no sorteio",
    "picker.teamsWord": "times",
    "picker.teamOne": "time",
    "picker.startsWith": "começa",
    "picker.badTeams": "Nº de times: um número inteiro de 2 a 20.",
    "picker.badPool": "Lista: até 100 nomes, cada um com até 48 caracteres.",
    "picker.failed": "Não foi possível abrir o sorteio ({code}). Confira a lista — nomes com telefone ou CPF são recusados.",
    // Alvo (W6a, US-LV-13): quem recebe uma versão diferente da rodada.
    "target.title": "Alvo",
    "target.hint": "Marque quem recebe um brief privado na próxima rodada de enquete, quiz ou pergunta aberta. A sala não vê quem foi escolhido. Não vale para rascunhos da IA nem para a corrida.",
    "target.empty": "Ninguém na sala ainda.",
    "target.brief": "Brief privado",
    "target.briefPlaceholder": "Só quem estiver marcado vai ler isto.",
    "target.count": "{n} pessoas recebem uma versão privada",
    "target.countOne": "1 pessoa recebe uma versão privada",
    "target.tooLong": "O brief privado tem no máximo 500 caracteres.",
    "target.rosterError": "Não foi possível carregar quem está na sala. Entre de novo se a sessão expirou.",
    "target.private": "Só você vê isso",
    "roster.away": "fora da sala",
    // times + placar por time + quem responde (W7, US-LV-14)
    "teams.title": "Times",
    "teams.hint": "Divida a sala em times e acompanhe o placar por time ao lado do individual. Cada pessoa só descobre o próprio time — a sala nunca vê quem é de quem.",
    "teams.count": "Quantos times",
    "teams.draw": "Sortear times",
    "teams.defaultName": "Time {n}",
    "teams.name": "Nome do time {n}",
    "teams.secret": "Membros em segredo",
    "teams.secretHint": "Isto muda só a sua tela: a sala nunca recebe a lista de nenhum time, marcado ou não.",
    "teams.aggregate": "Pontuação do time",
    "teams.sum": "Soma dos pontos",
    "teams.mean": "Média dos pontos (ninguém carrega o time sozinho)",
    "teams.save": "Salvar times",
    "teams.clear": "Desfazer times",
    "teams.saved": "Times salvos.",
    "teams.empty": "Ninguém na sala ainda.",
    "teams.failed": "Não foi possível salvar os times ({code}).",
    "teams.standings": "Placar por time",
    "teams.size": "{n} pessoas",
    "teams.sizeOne": "1 pessoa",
    "teams.leader": "Time na frente",
    "teams.yourTeam": "Seu time: {name}",
    "teams.myBoard": "Você é o nº {r} de {n} no seu time · time nº {tr} de {tc}",
    "teams.floorTitle": "Quem responde",
    "teams.floorHint": "Vale para enquete, quiz, nuvem de palavras, perguntas (Q&A) e rodada de frases. Nunca vai em sorteio, rascunho de IA nem corrida, e limpa sozinho quando a rodada é enviada. Quem ficou de fora continua podendo apoiar (▲) as perguntas.",
    "teams.floorAll": "Todo mundo responde",
    "teams.floorCount": "{n} pessoas podem responder",
    "teams.floorCountOne": "1 pessoa pode responder",
    "teams.readOnly": "Só quem foi escolhido responde esta rodada",
    "teams.membersHidden": "membros ocultos",
    "teams.noMembers": "sem ninguém ainda",
    // final / segundo turno (W8a, US-LV-15): o resultado de uma rodada vira o conteúdo da próxima.
    "derive.title": "Final",
    "derive.hint": "Abre uma rodada nova com as respostas mais votadas desta. Ninguém digita nada: o resultado desta rodada vira as opções da próxima.",
    "derive.k": "Quantas opções",
    "derive.prompt": "Pergunta da final",
    "derive.time": "Tempo",
    "derive.timeOff": "Sem tempo",
    "derive.timeSeconds": "{n}s",
    "derive.open": "Abrir final",
    "derive.promptRunoff": "Segundo turno: qual fica?",
    "derive.promptFinal": "Final: qual é a melhor?",
    "derive.promptWords": "Qual palavra representa melhor?",
    "derive.failed": "Não foi possível abrir a final ({code}).",
    "derive.e.SOURCE_NOT_CLOSED": "feche a rodada antes",
    "derive.e.CANNOT_DERIVE_FROM_TYPE": "um sorteio não tem respostas para levar",
    "derive.e.CANNOT_DERIVE_FROM_MEAN": "as opções de uma escala são notas, não candidatas",
    "derive.e.NOT_ENOUGH_CANDIDATES": "menos de duas respostas para escolher",
    // priorização (W9a, US-LV-16): votação por pontos, orçamento fixo e ranking — payloads de mc.
    "prio.title": "Priorização",
    "prio.hint": "A sala distribui pontos entre as opções ou coloca em ordem. É uma enquete: mesmas barras, sem tipo novo de atividade.",
    "prio.prompt": "Pergunta",
    "prio.presetPrompt": "No que a gente investe primeiro?",
    "prio.options": "Opções (uma por linha)",
    "prio.optionsPlaceholder": "Onboarding\nRelatórios\nIntegrações",
    "prio.mode": "Modo",
    "prio.modePoints": "Pontos",
    "prio.modeRank": "Ranking",
    "prio.budget": "Orçamento",
    "prio.budgetPoints": "{n} pontos",
    "prio.budgetFixed": "Orçamento fixo (100)",
    "prio.open": "Abrir priorização",
    "prio.failed": "Não foi possível abrir a priorização ({code}).",
    "prio.badPrompt": "Escreva a pergunta da rodada.",
    "prio.badOptions": "De 2 a 10 opções, uma por linha, com até 120 caracteres cada.",
    "prio.e.PAYLOAD_MAX_OPTIONS": "no máximo 10 opções — um celular não ordena catorze coisas",
    "prio.e.INVALID_BUDGET": "o orçamento é um número inteiro de 1 a 100",
    "prio.e.PAYLOAD_UNSCORED": "priorização não vale pontos: não existe resposta certa",
    "prio.e.VARIANT_OPTIONS_ON_PAYLOAD": "o alvo pode mudar o enunciado, não as opções",
    "prio.unit": "pontos",
    "prio.remaining": "restam {n} de {b} pontos",
    "prio.less": "menos {label}, {n} pontos",
    "prio.lessOne": "menos {label}, 1 ponto",
    "prio.more": "mais {label}, {n} pontos",
    "prio.moreOne": "mais {label}, 1 ponto",
    "prio.moved": "{label}: posição {i} de {n}",
    "prio.up": "subir {label}",
    "prio.down": "descer {label}",
    // escala com média (W9b, US-LV-5): as opções são os pontos da escala e o resultado é a MÉDIA.
    "scale.title": "Escala",
    "scale.hint": "As opções são os pontos da escala e o resultado é a média — uma enquete de sempre, com a média ao lado das barras.",
    "scale.prompt": "Pergunta",
    "scale.presetPrompt": "De 1 a 5, como você está chegando?",
    "scale.from": "De",
    "scale.to": "até",
    "scale.points": "{n} pontos",
    "scale.open": "Abrir escala",
    "scale.mean": "média {v}",
    "scale.meanLabel": "média",
    "scale.failed": "Não foi possível abrir a escala ({code}).",
    "scale.badPrompt": "Escreva a pergunta da rodada.",
    "scale.badRange": "A escala vai de 2 a 11 pontos, começando abaixo do fim.",
    "hosted.title": "Tela única (sem celular)",
    "hosted.hint": "A turma decide em conjunto olhando a projeção, e você registra a escolha com um toque. Ninguém precisa de celular nem de rede, e nenhum dado de participante é criado.",
    "hosted.prompt": "Pergunta",
    "hosted.options": "Opções (uma por linha, de 2 a 6)",
    "hosted.open": "Abrir em tela única",
    "hosted.failed": "Não foi possível abrir a rodada em tela única ({code}).",
    "hosted.badPrompt": "Escreva a pergunta da rodada.",
    "hosted.badOptions": "Informe de 2 a 6 opções diferentes, uma por linha.",
    "hosted.recordTitle": "A turma decidiu? Registre a escolha",
    "hosted.record": "Registrar: {label}",
    "hosted.noChoice": "Encerrar sem escolha",
    "hosted.recordFailed": "Não foi possível registrar a escolha ({code}). A rodada continua aberta.",
    "hosted.caption": "Tela única: a turma decide em conjunto e o host registra a escolha.",
    "hosted.recorded": "Escolha da turma registrada pelo host",
    "hosted.badge": "Rodada do host · tela única, sem participantes",
    "hosted.phone": "Esta rodada acontece na tela: a turma decide em conjunto.",
    "hosted.e.INVALID_HOSTED_CHOICE": "essa opção não existe ou a rodada não é de tela única",
    "hosted.e.HOSTED_NEEDS_PLAIN_MC": "tela única não combina com pontuação, prioridade, média ou resultado oculto",
    "hosted.e.HOSTED_IS_UNTIMED": "tela única não tem cronômetro: quem encerra é o host",
    "hosted.e.HOSTED_NOT_TARGETED": "tela única não tem alvo nem lista de elegíveis",
    "hosted.e.INVALID_HOSTED": "tela única só existe em enquete de múltipla escolha",
    "scale.e.AGGREGATE_ONLY_ON_MC": "só uma enquete tem média: nuvem, mural, urna e sorteio não",
    "scale.e.INVALID_AGGREGATE": "a média é o único agregado além da soma",
    "scale.e.MEAN_NEEDS_VALUES": "cada opção precisa do ponto da escala que representa",
    "scale.e.VARIANT_OPTIONS_ON_MEAN": "o alvo pode mudar o enunciado, não a escala",
    "scale.e.MEAN_UNSCORED": "escala não vale pontos: uma média não tem resposta certa",
    "scale.e.MEAN_NEEDS_ONE": "por enquanto a média é de um assunto por rodada, com um toque só",
    "picker.weightedRoomOnly": "Só para quem está na sala — com uma lista, o sorteio é simples.",

    "mc.answered": "responderam",
    "mc.answeredOne": "respondeu",
    "mc.tallyHidden": "resultado só no fim da rodada",
    "mc.viewLabel": "Ver resultado como",
    "mc.viewBars": "Barras",
    "mc.viewDonut": "Rosca",
    "error.MODERATION_REFUSED":
      "Esta moderação não foi aplicada: o limite de uma resposta por pessoa impede devolver esta pergunta ao telão, ou ela já não está nesta rodada.",
    "review.title": "Em revisão",
    "review.hint": "Nada aqui está no telão nem nos celulares. Aprove para publicar.",
    "review.approve": "Aprovar",
    "review.dismiss": "Descartar",
    "grp.button": "Agrupar por IA",
    "grp.working": "Agrupando…",
    "grp.title": "Categorias (só você vê)",
    "grp.hint": "A IA lê as respostas visíveis, sem nomes, e propõe categorias. Nada vai ao telão nem aos celulares.",
    "grp.skipped": "{n} resposta(s) não foram enviadas à IA (vazias ou com cara de dado pessoal).",
    "error.GROUPING_FAILED": "Não foi possível agrupar agora. A rodada continua como estava. Tente de novo em instantes.",
    "error.NOTHING_TO_GROUP": "Ainda não há respostas suficientes para agrupar (mínimo de duas).",
    "error.NOT_AN_OPEN_ROUND": "O agrupamento por IA só vale para rodadas de perguntas abertas.",
    "error.AI_NOT_CONFIGURED": "O agrupamento por IA não está disponível neste ambiente.",
    "a11y.liveResults": "Resultados ao vivo",
    "a11y.words": "palavras",
    "a11y.wordOne": "palavra",
    "a11y.questions": "perguntas",
    "a11y.questionOne": "pergunta",
    "a11y.mostCited": "Mais citada",
    "a11y.leader": "Líder",
    "a11y.correct": "resposta correta",
    "a11y.winner": "mais votada",
    "a11y.highlighted": "destacada pelo facilitador",
    "a11y.inReview": "em revisão",
    "pv.hide": "Ocultar",
    "pv.open": "Rodada de respostas",
    "pv.presetPrompt": "A pior desculpa para faltar numa reunião?",
    "pv.err.EMPTY": "Escreva algo antes de enviar.",
    "pv.err.LOOKS_LIKE_PII": "Parece um CPF ou telefone — não envie dados pessoais.",
    "pv.err.SELF_VOTE": "Não vale votar na sua própria resposta.",
    "pv.err.ALREADY_VOTED": "Você já votou nesta rodada.",
    "pv.err.NOT_ON_BALLOT": "Essa resposta não está mais disponível.",
    "pv.err.WRONG_PHASE": "O tempo desta etapa acabou.",
    "pv.err.RATE_LIMITED": "Devagar! Tente de novo em instantes.",
    "pv.err.NOT_ELIGIBLE": "Esta rodada não está aceitando a sua resposta.",
    // Last resort: `t` returns the KEY when a code has no string, and "pv.err.REJECTED" is not a
    // sentence to show someone holding a phone. Every code reaches a person, named or not.
    "pv.err.fallback": "Não foi possível enviar. Tente de novo.",
    // join / participant
    "join.title": "Entrar na sessão",
    "join.nickname": "Seu apelido",
    "join.cta": "Entrar",
    "join.seatExpired": "Sua sessão expirou. Entre de novo.",
    "join.longRunningRoom": "Esta sala fica aberta por vários dias — seu lugar, seu time e seus pontos ficam salvos NESTE navegador até {date}, ou até você sair da sala.",
    "join.longRunningRoomNoDate": "Esta sala fica aberta por vários dias — seu lugar, seu time e seus pontos ficam salvos NESTE navegador até o encerramento da sessão, ou até você sair da sala.",
    "join.invalidCode": "Código inválido.",
    "join.notFound": "Sessão não encontrada ou encerrada.",
    "join.rateLimited": "Muitas tentativas. Aguarde um momento.",
    "join.busyRetrying": "Muita gente entrando agora; tentando de novo em alguns segundos.",
    "join.nicknameRequired": "Digite um apelido.",
    "join.nicknamePii": "Esse apelido parece um CPF ou telefone — use um nome ou apelido.",
    "participant.waiting": "Aguardando o facilitador iniciar…",
    "participant.submitted": "Resposta enviada!",
    "participant.typeWord": "Digite uma palavra",
    "participant.typeAnswer": "Escreva sua resposta",
    "participant.send": "Enviar",
    // dials por rodada (US-LV-20) — código no fio, frase aqui (#6)
    "wc.quota": "Restam {n} de {max} palavras.",
    "panel.namedNotice": "Nesta rodada seu apelido aparece do lado da sua frase, para a sala toda.",
    // fila offline (W10b, US-LV-18) — uma contagem de verdade, nunca um spinner
    "participant.queued": "{n} respostas serão enviadas quando a conexão voltar.",
    "participant.queuedOne": "1 resposta será enviada quando a conexão voltar.",
    "participant.queueFull": "Já são {n} respostas esperando nesta rodada. Espere a conexão voltar para enviar mais.",
    "participant.unsentClosed": "A rodada fechou antes da conexão voltar.",
    "participant.leaveRoom": "Sair desta sala",
    "round.openUntil": "Aberta até {date}",
    "round.answeredComeBack": "Resposta enviada. Volte por este mesmo link quando o facilitador abrir a próxima.",
    "participant.roomFull": "A sala está cheia no momento. Continuamos tentando entrar; fique nesta tela.",
    "participant.offlineHint": "Sem conexão com a sala — tente os dados móveis.",
    // presenter
    "presenter.title": "Painel do facilitador",
    "presenter.create": "Criar sessão",
    "presenter.longRunning": "Sessão de longa duração (dias)",
    "presenter.longRunningHint": "A sala fica de pé por semanas: o lugar de cada pessoa e o placar atravessam os dias.",
    "series.title": "Tendência da série {id}",
    "series.loading": "Carregando a tendência…",
    "series.unavailable": "Não foi possível carregar a tendência agora. Tente novamente em instantes.",
    "series.empty": "Ainda não há pontos. A linha aparece quando duas sessões da série fecharem a mesma pergunta de escala com 5 ou mais respondentes.",
    "series.needTwo": "Um ponto só ainda: falta outra sessão da série com a mesma pergunta.",
    "series.chartLabel": "Média por sessão: {prompt}",
    "series.col.date": "Fechada em",
    "series.col.mean": "Média",
    "series.col.respondents": "Respondentes",
    "series.withheld": "{n} ponto(s) escondido(s): sessão com menos de 5 respondentes.",
    "series.minRespondents": "Só médias cruzam sessões, nunca respostas de pessoas. A pergunta é a mesma quando o texto é igual, sem contar maiúsculas, acentos e pontuação.",
    "series.link": "Ver tendência da série",
    "presenter.series": "Série (opcional)",
    "presenter.seriesHint": "Use o mesmo nome em cada encontro para ver a média da mesma pergunta de escala ao longo do tempo. Letras, números, hífen e sublinhado, até 64.",
    "presenter.seriesInvalid": "Use só letras sem acento, números, hífen e sublinhado (até 64). Sem isso, a sessão é criada sem série.",
    "presenter.horizon": "Link válido até {date}",
    "presenter.attendance": "{answered} de {enrolled} responderam",
    "presenter.roundWindow": "Fecha em",
    "presenter.windowOff": "Eu fecho",
    "presenter.windowHours": "{n} h",
    "presenter.windowDays": "{n} dias",
    // dials por tipo de rodada (US-LV-20) — cada tipo mostra só os seus
    "presenter.roundOptions": "Opções da próxima rodada",
    "presenter.wordsPerPerson": "Palavras por pessoa",
    "presenter.wordsUnlimited": "Sem limite",
    "presenter.showLive": "Mostrar ao vivo",
    "presenter.oneEntryPerPerson": "Uma resposta por pessoa",
    "presenter.namedPanel": "Painel com nome",
    "presenter.namedPanelHint": "O apelido de quem escreveu aparece do lado da frase, e a sala inteira vê. O celular avisa antes de enviar.",
    "presenter.showLiveTypes": "Vale para enquete, nuvem de palavras e perguntas. Votação de ideias e sorteio têm o próprio ritmo.",
    "error.ROUND_TOO_LONG": "Esta sessão só aceita rodadas de até 10 minutos.",
    "error.ROUND_OUTLIVES_SESSION": "A rodada fecharia depois do fim da sessão. Encurte a rodada ou crie uma sessão mais longa.",
    "error.ASYNC_NEEDS_FLAT_SCORING": "Numa rodada de vários dias a pontuação por velocidade premia quem abriu o e-mail primeiro. Use pontuação por acerto.",
    "error.INVALID_MAX_WORDS": "O limite de palavras por pessoa só existe na nuvem de palavras, e vai de 1 a 3.",
    "error.INVALID_PANEL": "Uma entrada por pessoa e painel com nome só existem nas perguntas e respostas.",
    "error.INVALID_REVEAL_TALLY": "\"Mostrar ao vivo\" vale para enquete, nuvem de palavras e perguntas — não para votação de ideias nem para sorteio.",
    "presenter.code": "Código de entrada",
    "presenter.participants": "Participantes",
    "presenter.open": "Abrir",
    "presenter.close": "Encerrar pergunta",
    "presenter.reveal": "Revelar",
    "presenter.raceStart": "Iniciar corrida de perguntas",
    "presenter.raceNext": "Próxima rodada",
    "presenter.raceFinish": "Encerrar e mostrar o pódio",
    "presenter.raceRound": "Rodada",
    "presenter.raceEnded": "Corrida encerrada",
    "presenter.raceHint": "Várias perguntas cronometradas em sequência, com ranking acumulado.",
    "presenter.racePreset1": "Quanto é 1 + 1?",
    "presenter.racePreset2": "Qual é a capital do Brasil?",
    "presenter.racePreset3": "Quantos dias tem uma semana?",
    "presenter.poll": "Enquete",
    "presenter.quiz": "Quiz pontuado",
    "presenter.scoring": "Pontuação do quiz",
    "presenter.scoringAccuracy": "Precisão — pontos fixos por acerto",
    "presenter.scoringSpeed": "Velocidade — acertar rápido vale mais",
    "presenter.scoringSelfPaced": "Sem cronômetro — cada um no seu ritmo",
    "presenter.wordcloud": "Nuvem de palavras",
    "presenter.qa": "Perguntas e respostas",
    "presenter.projection": "Abrir projeção",
    "presenter.aiTitle": "Gerar quiz com IA (rascunho)",
    "presenter.aiTopic": "Tema do quiz",
    "presenter.aiGenerate": "Gerar rascunhos",
    "presenter.aiOpen": "Abrir",
    "presenter.aiHint": "A IA propõe; você revisa e abre. Nada é publicado automaticamente.",
    "presenter.devLogin": "Login dev rápido",
    "projection.joinPrompt": "Entre em {url} com o código",
    "projection.waiting": "Aguardando a primeira pergunta…",
    "common.leaderboard": "Ranking",
    "common.votes": "votos",
    "common.responses": "respostas",
    "common.closed": "Pergunta encerrada",
    // login (magic link)
    "login.title": "Entrar no Klapzo",
    "login.subtitle": "Enviaremos um link de acesso para o seu e-mail. Sem senha.",
    "login.email": "Seu e-mail",
    "login.cta": "Enviar link de acesso",
    "login.sent": "Link enviado!",
    "login.sentDesc": "Se houver uma conta com esse e-mail, o link de acesso chegou na caixa de entrada. Vale por 30 minutos.",
    "login.again": "Enviar novamente",
    "login.err.link": "Link inválido. Tente entrar novamente.",
    "login.err.expirado": "Este link expirou ou já foi usado. Peça um novo.",
    "login.err.conta": "Não encontramos sua conta. Fale com o administrador.",
    "login.home": "← Voltar ao início",
    // topbar / nav
    "topbar.home": "Início",
    "topbar.host": "Facilitar",
    "topbar.kit": "Kit",
    "topbar.logout": "Sair",
    // kit
    "nav.library": "Biblioteca",
    "nav.planner": "Agenda",
    "library.title": "Métodos de facilitação",
    "library.search": "Buscar métodos…",
    "library.allModes": "Todos os formatos",
    "library.add": "+ Agenda",
    "library.sort": "Ordenar",
    "library.saveSet": "Salvar filtros",
    "library.saveSetPrompt": "Nome para este conjunto de filtros:",
    "library.deleteSet": "Excluir conjunto",
    "library.count": "métodos",
    "library.countOne": "método",
    "sort.title": "Título (A–Z)",
    "sort.duration-asc": "Mais curtos primeiro",
    "sort.duration-desc": "Mais longos primeiro",
    "sort.group-asc": "Grupos menores primeiro",
    "sort.group-desc": "Grupos maiores primeiro",
    "detail.related": "Métodos relacionados",
    "library.empty": "Nenhum método encontrado.",
    "kit.loading": "Carregando…",
    "kit.error": "Não foi possível carregar a biblioteca agora.",
    "kit.retry": "Tentar de novo",
    "library.onlyFavourites": "Só favoritos",
    "detail.favourite": "Favoritar",
    "detail.unfavourite": "Remover dos favoritos",
    "mode.online": "Online",
    "mode.presencial": "Presencial",
    "mode.hibrido": "Híbrido",
    "detail.materials": "Materiais",
    "detail.source": "Fonte",
    "detail.back": "← Biblioteca",
    "detail.add": "Adicionar à agenda",
    "planner.title": "Planejador de agenda",
    "planner.empty": "Adicione métodos da biblioteca para montar sua agenda.",
    "planner.start": "Início",
    "planner.total": "Duração total",
    "planner.export": "Exportar",
    "planner.exportCsv": "CSV",
    "planner.exportDocx": "Word (.docx)",
    "planner.docxMethod": "Método",
    "planner.docxFacOnly": "só facilitador",
    "planner.printPdf": "Imprimir / PDF",
    "planner.csvOrder": "Nº",
    "planner.csvBlock": "Bloco",
    "planner.csvDuration": "Duração",
    "planner.print": "Imprimir",
    "planner.remove": "Remover",
    "planner.min": "min",
    "planner.clock.title": "Relógio de entrega",
    "planner.clock.start": "Iniciar entrega",
    "planner.clock.next": "Próximo bloco",
    "planner.clock.stop": "Encerrar entrega",
    "planner.clock.onAir": "No ar: {block}",
    "planner.clock.remaining": "{n} min restantes",
    "planner.clock.blockOver": "Bloco estourado em {n} min",
    "planner.clock.agendaOver": "Agenda estourada em {n} min",
    "planner.clock.agendaAhead": "Agenda adiantada em {n} min",
    "planner.clock.onTime": "Agenda no horário",
    "planner.clock.done": "Agenda terminada",
    "planner.clock.live": "Relógio de entrega",
    "planner.dragHint": "Arraste para reordenar — os horários recalculam sozinhos.",
    "planner.save": "Salvar",
    "planner.saveNew": "Salvar na minha conta",
    "planner.savedOk": "Agenda salva na sua conta.",
    "planner.saveFailed": "Não foi possível salvar agora — sua agenda continua neste navegador.",
    "planner.saveRefused": "O servidor recusou este save ({code}) — sua agenda continua neste navegador.",
    "planner.localOnly": "Esta agenda fica só neste navegador. Entre na sua conta para salvá-la.",
    "planner.linked": "Salva na sua conta. Alterações só vão para o servidor quando você salvar.",
    "planner.unsaved": "Rascunho neste navegador — ainda não salvo na sua conta.",
    "planner.myAgendas": "Minhas agendas",
    "planner.open": "Abrir",
    "planner.delete": "Excluir",
    "planner.duplicate": "Duplicar",
    "planner.share": "Compartilhar",
    "planner.unshare": "Parar de compartilhar",
    "planner.shareLink": "Link de leitura",
    "planner.shareHint": "Quem tiver o link vê a agenda, sem entrar. Pare de compartilhar para desativá-lo.",
    "planner.sharedBadge": "compartilhada",
    "planner.facOnly": "Só facilitador",
    "planner.facOnlyHint": "Marcado: este bloco fica de fora do link compartilhado e do que o visitante imprime ou baixa. Vale depois de salvar.",
    "planner.shareConfirm": "Compartilhar \"{title}\"? {hidden} de {total} bloco(s) estão como \"só facilitador\" e ficam de fora do link. Os outros {visible} aparecem para quem tiver o link, sem entrar. Ao confirmar, a agenda é salva e o link serve essa versão. Os horários dos outros blocos não mudam, então o intervalo aparece.",
    "planner.shareConfirmNone": "Compartilhar \"{title}\"? Nenhum bloco está como \"só facilitador\": quem tiver o link vê os {total} bloco(s). Ao confirmar, a agenda é salva e o link serve essa versão. Os horários dos outros blocos não mudam, então o intervalo aparece.",
    "share.print": "Imprimir / PDF",
    "share.csv": "CSV",
    "share.docx": "Word (.docx)",
    // sessão ao vivo no planejador + "Rodar agora" por bloco (US-MB-6, W12 fatia 1)
    "live.title": "Sessão ao vivo",
    "live.signedOut": "Rodar um bloco ao vivo exige entrar na sua conta. O planejador continua funcionando neste navegador.",
    "live.create": "Criar sessão",
    "live.codeLabel": "ou entre com o código",
    "live.attach": "Usar este código",
    "live.detach": "Desconectar a sala",
    "live.joinCode": "Código da sala",
    "live.joinLink": "Link para os participantes",
    "live.projection": "Abrir projeção",
    "live.run": "Rodar agora",
    "live.noSpec": "Esta ficha ainda não traz uma rodada ao vivo.",
    "live.noSession": "Crie ou informe uma sala para rodar este bloco.",
    "live.onAir": "no ar — você fecha quando quiser",
    "live.onAirTimed": "no ar · {min} min",
    "live.badCode": "Código inválido. São 6 caracteres, como aparecem na projeção.",
    "live.roomNotFound": "Não encontramos essa sala aberta. Confira o código ou crie uma sala.",
    "live.refused": "A rodada não abriu (código {code}). Nada foi enviado para a sala.",
    "live.close": "Fechar rodada",
    "live.closedRound": "encerrada",
    "live.superseded": "substituída pela rodada seguinte — a sala mantém uma rodada aberta por vez, o que já valia antes desta tela",
    "live.unknownOutcome": "Não sabemos o que a sala está exibindo: o envio não foi confirmado. Confira a projeção antes de clicar de novo.",
    "live.notHost": "Só quem tem papel de facilitador (host ou owner) pode abrir uma sala ao vivo.",
    "live.createFailed": "Não foi possível criar a sala (código {code}). Nenhuma sala foi aberta.",
    "live.attachFailed": "Não foi possível usar esse código (código {code}). Nada mudou na sua agenda.",
    "live.closeFailed": "Não foi possível fechar a rodada (código {code}). Ela pode continuar aberta na sala.",
    "live.results": "Resultados",
    "live.resultsFailed": "Não foi possível ler os resultados (código {code}).",
    "live.noRounds": "Os resultados aparecem quando a rodada fecha.",
    "live.notRecorded": "Um sorteio não fica registrado: a sala guarda o resultado das rodadas, não dos sorteios.",
    "live.hiddenTally": "Contagem oculta durante a rodada · respostas",
    "live.armed": "a próxima é esta — rode quando quiser",
    "share.readOnly": "Agenda compartilhada — somente leitura.",
    "share.gone": "Link indisponível",
    "share.goneHint": "Este link não existe ou foi desativado por quem criou a agenda.",
  },
  "en-US": {
    // results dashboard (durable per-round metrics)
    "results.title": "Session results",
    "results.back": "Back",
    "results.loading": "Loading results…",
    "results.empty": "No rounds closed yet. Results appear here as soon as you close your first question.",
    "results.unavailable": "Results could not be loaded right now. Try again in a moment.",
    "results.notFound": "Session not found.",
    "results.rounds": "rounds",
    "results.totalResponses": "responses in total",
    "results.peakResponses": "busiest round",
    "results.peakHint": "Most responses in any single round — not a count of people in the room.",
    "results.responses": "responses",
    "results.responseOne": "response",
    "results.scored": "Scored",
    "results.round": "Round",
    "results.export": "Download CSV",
    "results.exportPdf": "Download PDF report",
    "results.exportLocked": "Export is available on paid plans",
    "results.exportLockedHint": "This result was produced on a free plan. Export stays locked even after you subscribe — the people in that room were told so.",
    "results.withheld": "{n} round(s) outside your plan's retention window ({days} days).",
    "results.withheldHint": "Paid plans keep results longer.",
    "results.anonymous": "Authors hidden",
    "results.report": "Print this page",
    "results.reportHeader": "Klapzo · Session report {id} · generated {date}",
    // plans + upgrade (where every 402 lands)
    "plan.free": "Free",
    "plan.facilitador": "Facilitator",
    "plan.equipe": "Team",
    "plan.evento": "Event",
    "plan.corporativo": "Corporate",
    "upgrade.title": "Klapzo plans",
    "upgrade.loading": "Loading plans…",
    "upgrade.unavailable": "Couldn't load plans right now. Try again in a moment.",
    "upgrade.back": "Back",
    "upgrade.current": "Current plan",
    "upgrade.currentPlan": "Your plan",
    "upgrade.buyMonthly": "Subscribe for {money}/month",
    "upgrade.buyAnnual": "Subscribe for {money}/year",
    "upgrade.buyOneOff": "Buy for {money} — {days} days",
    "upgrade.redirecting": "Redirecting…",
    "upgrade.emailPrompt": "Billing email:",
    "upgrade.emailInvalid": "Enter a valid email to continue.",
    "upgrade.checkoutFailed": "Couldn't start the payment. Try again in a moment.",
    "upgrade.pastDue": "Payment overdue.",
    "upgrade.pastDueHint": "Your {plan} subscription is past due, so rooms have returned to free-plan limits. Settle it to restore access.",
    "upgrade.passActive": "{plan} pass active until {date}.",
    "upgrade.talkToSales": "Talk to sales",
    "upgrade.taxNote": "Prices in Brazilian reais, payable by PIX, boleto or card via Mercado Pago.",
    "upgrade.corporativoBlurb": "From R$ 890/month. Rooms up to 1,000 people, 25 seats, indefinite retention, SSO and white-label.",
    "upgrade.blurb.facilitador": "Unlimited rounds, up to 100 participants per room, CSV/JSON export and the full Klapzo Kit.",
    "upgrade.blurb.equipe": "Everything in Facilitator, plus 5 seats, 5 concurrent rooms and 200 participants per room.",
    "upgrade.blurb.evento": "A standalone 7-day pass for a conference: 300 participants per room, no subscription.",
    "limit.roundsTitle": "Round limit reached",
    "limit.roundsBody": "The {plan} plan allows {limit} rounds per room. Upgrade to open unlimited rounds.",
    "limit.sessionsTitle": "Concurrent room limit reached",
    "limit.sessionsBody": "The {plan} plan allows {limit} room(s) at once. Close a room or upgrade.",
    "limit.asyncTitle": "Long-running sessions not available",
    "limit.asyncBody": "Long-running sessions are available from the Facilitador plan up. On your plan a room lasts {limit} hours.",
    "limit.cta": "See plans",
    "sessionOver.title": "This room has ended",
    "sessionOver.closed": "You ended this room. Create a new one to carry on.",
    "sessionOver.expired": "A room lasts at most 12 hours and this one reached the limit. Create a new one to carry on — its results are still available.",
    "results.votes": "votes",

    // promptvote (US-LV-10)
    "pv.title": "Answer round",
    "pv.write": "Write your answer",
    "pv.writeHint": "One line. Your answer goes on the shared screen — no personal data.",
    "pv.sent": "Answer sent! Voting is next.",
    "pv.waitingWrite": "The group is writing…",
    "pv.submissions": "answers in",
    "pv.submissionOne": "answer in",
    "pv.vote": "Vote for the best",
    "pv.voteHint": "You cannot vote for your own answer.",
    "pv.voted": "Vote counted!",
    "pv.votesCast": "votes cast",
    "pv.reveal": "Results",
    "pv.votes": "votes",
    "pv.voteOne": "vote",
    // honest ballot (W10a)
    "pv.omitted": "{n} answers did not fit on the ballot.",
    "pv.omittedOne": "1 answer did not fit on the ballot.",
    // joining from the screen (W10a)
    "join.instruction": "Point your camera, or type the code:",
    "join.qrLabel": "QR code to join the room with code {code}",
    "projection.more": "+{n} not shown",
    "projection.moreOne": "+1 not shown",
    // picker / "Sorteio" (US-LV-6, W5). The product name stays "Sorteio" in every locale.
    "picker.title": "Sorteio",
    "picker.open": "Sorteio",
    "picker.one": "Draw one person",
    "picker.weighted": "Draw whoever has taken part least",
    "picker.teams": "Draw teams",
    "picker.teamCount": "How many teams",
    "picker.rotation": "Rotation order",
    "picker.pool": "or draw from this list (one per line)",
    "picker.poolPlaceholder": "One name per line",
    "picker.spinHint": "The draw is made on the server when the round opens. Nobody — not even the facilitator — sees the result before the round closes.",
    "picker.spinning": "Drawing…",
    "picker.drawn": "Drawn",
    "picker.nobody": "Nobody to draw.",
    "picker.you": "It's you!",
    "picker.yourTeam": "Your team: {n}",
    "picker.yourPosition": "You are number {n}",
    "picker.team": "Team",
    "picker.candidates": "names in the draw",
    "picker.candidateOne": "name in the draw",
    "picker.teamsWord": "teams",
    "picker.teamOne": "team",
    "picker.startsWith": "starts with",
    "picker.badTeams": "Number of teams: a whole number from 2 to 20.",
    "picker.badPool": "List: up to 100 names, each up to 48 characters.",
    "picker.failed": "Could not open the draw ({code}). Check the list — names with a phone number or ID are refused.",
    // Target (W6a, US-LV-13): who gets a different version of the round.
    "target.title": "Target",
    "target.hint": "Tick who receives a private brief in the next poll, quiz or open question. The room never sees who was picked. AI drafts and the race are not targeted.",
    "target.empty": "Nobody in the room yet.",
    "target.brief": "Private brief",
    "target.briefPlaceholder": "Only the people you ticked will read this.",
    "target.count": "{n} people get a private version",
    "target.countOne": "1 person gets a private version",
    "target.tooLong": "A private brief is 500 characters at most.",
    "target.rosterError": "Could not load who is in the room. Sign in again if your session expired.",
    "target.private": "Only you can see this",
    "roster.away": "away",
    // teams + team standings + floor control (W7, US-LV-14)
    "teams.title": "Teams",
    "teams.hint": "Split the room into teams and watch the team board beside the individual one. Each person only ever learns their own team — the room never sees who is on which.",
    "teams.count": "How many teams",
    "teams.draw": "Draw teams",
    "teams.defaultName": "Team {n}",
    "teams.name": "Name of team {n}",
    "teams.secret": "Secret membership",
    "teams.secretHint": "This changes your screen only: the room never receives any team's member list, ticked or not.",
    "teams.aggregate": "Team score",
    "teams.sum": "Sum of points",
    "teams.mean": "Average points (nobody carries the team alone)",
    "teams.save": "Save teams",
    "teams.clear": "Remove teams",
    "teams.saved": "Teams saved.",
    "teams.empty": "Nobody in the room yet.",
    "teams.failed": "Could not save the teams ({code}).",
    "teams.standings": "Team board",
    "teams.size": "{n} people",
    "teams.sizeOne": "1 person",
    "teams.leader": "Leading team",
    "teams.yourTeam": "Your team: {name}",
    "teams.myBoard": "You are #{r} of {n} on your team · team #{tr} of {tc}",
    "teams.floorTitle": "Who answers",
    "teams.floorHint": "Applies to polls, quizzes, word clouds, Q&A and prompt rounds. Never goes on a draw, an AI draft or a race, and clears itself once the round is sent. Everyone left out can still upvote (▲) questions.",
    "teams.floorAll": "Everyone answers",
    "teams.floorCount": "{n} people may answer",
    "teams.floorCountOne": "1 person may answer",
    "teams.readOnly": "Only the chosen answer this round",
    "teams.membersHidden": "members hidden",
    "teams.noMembers": "nobody yet",
    // final / run-off (W8a, US-LV-15): one round's result becomes the next round's content.
    "derive.title": "Final",
    "derive.hint": "Opens a new round from this one's most-voted answers. Nobody types anything: this round's result becomes the next round's options.",
    "derive.k": "How many options",
    "derive.prompt": "Question for the final",
    "derive.time": "Time",
    "derive.timeOff": "No timer",
    "derive.timeSeconds": "{n}s",
    "derive.open": "Open the final",
    "derive.promptRunoff": "Run-off: which one stays?",
    "derive.promptFinal": "Final: which is best?",
    "derive.promptWords": "Which word says it best?",
    "derive.failed": "Could not open the final ({code}).",
    "derive.e.SOURCE_NOT_CLOSED": "close the round first",
    "derive.e.CANNOT_DERIVE_FROM_TYPE": "a draw has no answers to carry over",
    "derive.e.CANNOT_DERIVE_FROM_MEAN": "a scale's options are ratings, not candidates",
    "derive.e.NOT_ENOUGH_CANDIDATES": "fewer than two answers to choose from",
    // prioritisation (W9a, US-LV-16): dot voting, fixed budget and ranking — mc payloads.
    "prio.title": "Prioritisation",
    "prio.hint": "The room spreads points across the options, or puts them in order. It is a poll: same bars, no new activity type.",
    "prio.prompt": "Question",
    "prio.presetPrompt": "What do we invest in first?",
    "prio.options": "Options (one per line)",
    "prio.optionsPlaceholder": "Onboarding\nReporting\nIntegrations",
    "prio.mode": "Mode",
    "prio.modePoints": "Points",
    "prio.modeRank": "Ranking",
    "prio.budget": "Budget",
    "prio.budgetPoints": "{n} points",
    "prio.budgetFixed": "Fixed budget (100)",
    "prio.open": "Open prioritisation",
    "prio.failed": "Could not open the prioritisation ({code}).",
    "prio.badPrompt": "Write the round's question.",
    "prio.badOptions": "Between 2 and 10 options, one per line, up to 120 characters each.",
    "prio.e.PAYLOAD_MAX_OPTIONS": "ten options at most — a phone cannot rank fourteen things",
    "prio.e.INVALID_BUDGET": "the budget is a whole number from 1 to 100",
    "prio.e.PAYLOAD_UNSCORED": "a prioritisation scores nothing: there is no right answer",
    "prio.e.VARIANT_OPTIONS_ON_PAYLOAD": "a brief can change the question, not the options",
    "prio.unit": "points",
    "prio.remaining": "{n} of {b} points left",
    "prio.less": "less {label}, {n} points",
    "prio.lessOne": "less {label}, 1 point",
    "prio.more": "more {label}, {n} points",
    "prio.moreOne": "more {label}, 1 point",
    "prio.moved": "{label}: position {i} of {n}",
    "prio.up": "move {label} up",
    "prio.down": "move {label} down",
    // scale with a mean (W9b, US-LV-5): the options are the scale points, the result is the AVERAGE.
    "scale.title": "Scale",
    "scale.hint": "The options are the points of a scale and the result is their average — the usual poll, with the mean beside the bars.",
    "scale.prompt": "Question",
    "scale.presetPrompt": "From 1 to 5, how are you arriving?",
    "scale.from": "From",
    "scale.to": "to",
    "scale.points": "{n} points",
    "scale.open": "Open scale",
    "scale.mean": "mean {v}",
    "scale.meanLabel": "mean",
    "scale.failed": "Could not open the scale ({code}).",
    "scale.badPrompt": "Write the round's question.",
    "scale.badRange": "A scale runs from 2 to 11 points, starting below its end.",
    "hosted.title": "Single screen (no phones)",
    "hosted.hint": "The room decides together while looking at the projection, and you record the choice with one tap. Nobody needs a phone or a network, and no participant data is created.",
    "hosted.prompt": "Question",
    "hosted.options": "Options (one per line, 2 to 6)",
    "hosted.open": "Open on single screen",
    "hosted.failed": "Could not open the single-screen round ({code}).",
    "hosted.badPrompt": "Write the round's question.",
    "hosted.badOptions": "Give 2 to 6 different options, one per line.",
    "hosted.recordTitle": "Has the room decided? Record the choice",
    "hosted.record": "Record: {label}",
    "hosted.noChoice": "End without a choice",
    "hosted.recordFailed": "Could not record the choice ({code}). The round is still open.",
    "hosted.caption": "Single screen: the room decides together and the host records the choice.",
    "hosted.recorded": "Room's choice, recorded by the host",
    "hosted.badge": "Host round · single screen, no participants",
    "hosted.phone": "This round happens on the screen: the room decides together.",
    "hosted.e.INVALID_HOSTED_CHOICE": "that option does not exist or the round is not single-screen",
    "hosted.e.HOSTED_NEEDS_PLAIN_MC": "single screen cannot be scored, ranked, averaged or hide its result",
    "hosted.e.HOSTED_IS_UNTIMED": "single screen has no timer: the host ends it",
    "hosted.e.HOSTED_NOT_TARGETED": "single screen has no targeting or eligible list",
    "hosted.e.INVALID_HOSTED": "single screen exists only on a multiple-choice poll",
    "scale.e.AGGREGATE_ONLY_ON_MC": "only a poll has an average: a cloud, a wall, a ballot and a draw do not",
    "scale.e.INVALID_AGGREGATE": "the mean is the only aggregate besides the sum",
    "scale.e.MEAN_NEEDS_VALUES": "every option needs the scale point it stands for",
    "scale.e.VARIANT_OPTIONS_ON_MEAN": "a brief can change the question, not the scale",
    "scale.e.MEAN_UNSCORED": "a scale scores nothing: an average has no right answer",
    "scale.e.MEAN_NEEDS_ONE": "for now the average is one subject per round, one tap each",
    "picker.weightedRoomOnly": "Only for people in the room — with a list, the draw is uniform.",

    "mc.answered": "answered",
    "mc.answeredOne": "answered",
    "mc.tallyHidden": "results shown when the round closes",
    "mc.viewLabel": "Show results as",
    "mc.viewBars": "Bars",
    "mc.viewDonut": "Doughnut",
    "error.MODERATION_REFUSED":
      "This moderation was not applied: the round's one-answer-per-person limit blocks putting this question back on screen, or it is no longer in this round.",
    "review.title": "In review",
    "review.hint": "Nothing here is on the screen or on anyone's phone. Approve to publish.",
    "review.approve": "Approve",
    "review.dismiss": "Dismiss",
    "grp.button": "Group with AI",
    "grp.working": "Grouping…",
    "grp.title": "Categories (only you see this)",
    "grp.hint": "The AI reads the visible answers, with no names, and proposes categories. Nothing goes to the screen or to phones.",
    "grp.skipped": "{n} answer(s) were not sent to the AI (empty or look like personal data).",
    "error.GROUPING_FAILED": "Could not group right now. The round is unchanged. Try again in a moment.",
    "error.NOTHING_TO_GROUP": "There are not enough answers to group yet (two at least).",
    "error.NOT_AN_OPEN_ROUND": "AI grouping only works on open-question rounds.",
    "error.AI_NOT_CONFIGURED": "AI grouping is not available in this environment.",
    "a11y.liveResults": "Live results",
    "a11y.words": "words",
    "a11y.wordOne": "word",
    "a11y.questions": "questions",
    "a11y.questionOne": "question",
    "a11y.mostCited": "Most cited",
    "a11y.leader": "Leader",
    "a11y.correct": "correct answer",
    "a11y.winner": "most voted",
    "a11y.highlighted": "highlighted by the facilitator",
    "a11y.inReview": "in review",
    "pv.hide": "Hide",
    "pv.open": "Answer round",
    "pv.presetPrompt": "The worst excuse for missing a meeting?",
    "pv.err.EMPTY": "Write something before sending.",
    "pv.err.LOOKS_LIKE_PII": "That looks like an ID or phone number — don't send personal data.",
    "pv.err.SELF_VOTE": "You can't vote for your own answer.",
    "pv.err.ALREADY_VOTED": "You already voted this round.",
    "pv.err.NOT_ON_BALLOT": "That answer is no longer available.",
    "pv.err.WRONG_PHASE": "Time is up for this step.",
    "pv.err.RATE_LIMITED": "Slow down! Try again in a moment.",
    "pv.err.NOT_ELIGIBLE": "This round is not taking your answer.",
    "pv.err.fallback": "That did not go through. Try again.",
    "join.title": "Join the session",
    "join.nickname": "Your nickname",
    "join.cta": "Join",
    "join.seatExpired": "Your session expired. Join again.",
    "join.longRunningRoom": "This room stays open for days — your seat, your team and your points are saved IN THIS BROWSER until {date}, or until you leave the room.",
    "join.longRunningRoomNoDate": "This room stays open for days — your seat, your team and your points are saved IN THIS BROWSER until the session closes, or until you leave the room.",
    "join.invalidCode": "Invalid code.",
    "join.notFound": "Session not found or closed.",
    "join.rateLimited": "Too many attempts. Please wait a moment.",
    "join.busyRetrying": "Lots of people joining right now; trying again in a few seconds.",
    "join.nicknameRequired": "Enter a nickname.",
    "join.nicknamePii": "That nickname looks like a CPF or phone number — use a name instead.",
    "participant.waiting": "Waiting for the host to start…",
    "participant.submitted": "Answer submitted!",
    "participant.typeWord": "Type a word",
    "participant.typeAnswer": "Write your answer",
    "participant.send": "Send",
    "wc.quota": "{n} of {max} words left.",
    "panel.namedNotice": "In this round your nickname appears next to your sentence, for the whole room.",
    "participant.queued": "{n} answers will be sent when the connection is back.",
    "participant.queuedOne": "1 answer will be sent when the connection is back.",
    "participant.queueFull": "{n} answers are already waiting in this round. Wait for the connection to send more.",
    "participant.unsentClosed": "The round closed before the connection came back.",
    "participant.leaveRoom": "Leave this room",
    "round.openUntil": "Open until {date}",
    "round.answeredComeBack": "Answer sent. Come back through this same link when the host opens the next one.",
    "participant.roomFull": "The room is full right now. We keep trying to get you in; stay on this screen.",
    "participant.offlineHint": "No connection to the room — try mobile data.",
    "presenter.title": "Host panel",
    "presenter.create": "Create session",
    "presenter.longRunning": "Long-running session (days)",
    "presenter.longRunningHint": "The room stays up for weeks: everyone's seat and the scoreboard carry across the days.",
    "series.title": "Trend of series {id}",
    "series.loading": "Loading the trend…",
    "series.unavailable": "Could not load the trend right now. Try again in a moment.",
    "series.empty": "No points yet. The line appears once two sessions of the series close the same scale question with 5 or more respondents.",
    "series.needTwo": "Only one point so far: another session of the series needs the same question.",
    "series.chartLabel": "Mean per session: {prompt}",
    "series.col.date": "Closed on",
    "series.col.mean": "Mean",
    "series.col.respondents": "Respondents",
    "series.withheld": "{n} point(s) hidden: session with fewer than 5 respondents.",
    "series.minRespondents": "Only means cross sessions, never people's answers. A question is the same when the text matches, ignoring case, accents and punctuation.",
    "series.link": "See the series trend",
    "presenter.series": "Series (optional)",
    "presenter.seriesHint": "Use the same name at every meeting to see the mean of the same scale question over time. Letters, numbers, hyphen and underscore, up to 64.",
    "presenter.seriesInvalid": "Use only unaccented letters, numbers, hyphen and underscore (up to 64). Otherwise the session is created without a series.",
    "presenter.horizon": "Link valid until {date}",
    "presenter.attendance": "{answered} of {enrolled} answered",
    "presenter.roundWindow": "Closes in",
    "presenter.windowOff": "I close it",
    "presenter.windowHours": "{n} h",
    "presenter.windowDays": "{n} days",
    "presenter.roundOptions": "Options for the next round",
    "presenter.wordsPerPerson": "Words per person",
    "presenter.wordsUnlimited": "No limit",
    "presenter.showLive": "Show live",
    "presenter.oneEntryPerPerson": "One answer per person",
    "presenter.namedPanel": "Named panel",
    "presenter.namedPanelHint": "The author's nickname appears next to the sentence and the whole room sees it. The phone says so before sending.",
    "presenter.showLiveTypes": "Applies to polls, word clouds and Q&A. Idea voting and the draw have their own pacing.",
    "error.ROUND_TOO_LONG": "This session only accepts rounds up to 10 minutes.",
    "error.ROUND_OUTLIVES_SESSION": "The round would close after the session ends. Shorten it, or create a longer session.",
    "error.ASYNC_NEEDS_FLAT_SCORING": "Over several days, speed scoring rewards whoever opened the email first. Use accuracy scoring.",
    "error.INVALID_MAX_WORDS": "A words-per-person limit only exists on a word cloud, and it goes from 1 to 3.",
    "error.INVALID_PANEL": "One entry per person and the named panel only exist on Q&A.",
    "error.INVALID_REVEAL_TALLY": "\"Show live\" applies to polls, word clouds and Q&A — not to idea voting or the draw.",
    "presenter.code": "Join code",
    "presenter.participants": "Participants",
    "presenter.open": "Open",
    "presenter.close": "Close question",
    "presenter.reveal": "Reveal",
    "presenter.raceStart": "Start trivia race",
    "presenter.raceNext": "Next round",
    "presenter.raceFinish": "Finish and show the podium",
    "presenter.raceRound": "Round",
    "presenter.raceEnded": "Race finished",
    "presenter.raceHint": "Several timed questions in sequence, with a cumulative leaderboard.",
    "presenter.racePreset1": "What is 1 + 1?",
    "presenter.racePreset2": "What is the capital of Brazil?",
    "presenter.racePreset3": "How many days are in a week?",
    "presenter.poll": "Poll",
    "presenter.quiz": "Scored quiz",
    "presenter.scoring": "Quiz scoring",
    "presenter.scoringAccuracy": "Accuracy — flat points per correct answer",
    "presenter.scoringSpeed": "Speed — faster correct answers score more",
    "presenter.scoringSelfPaced": "Self-paced — no clock",
    "presenter.wordcloud": "Word cloud",
    "presenter.qa": "Q&A",
    "presenter.projection": "Open projection",
    "presenter.aiTitle": "Generate quiz with AI (draft)",
    "presenter.aiTopic": "Quiz topic",
    "presenter.aiGenerate": "Generate drafts",
    "presenter.aiOpen": "Open",
    "presenter.aiHint": "AI proposes; you review and open. Nothing is auto-published.",
    "presenter.devLogin": "Quick dev login",
    "projection.joinPrompt": "Join at {url} with the code",
    "projection.waiting": "Waiting for the first question…",
    "common.leaderboard": "Leaderboard",
    "common.votes": "votes",
    "common.responses": "responses",
    "common.closed": "Question closed",
    "login.title": "Sign in to Klapzo",
    "login.subtitle": "We'll email you a sign-in link. No password.",
    "login.email": "Your email",
    "login.cta": "Send sign-in link",
    "login.sent": "Link sent!",
    "login.sentDesc": "If an account exists for that email, the sign-in link is in the inbox. Valid for 30 minutes.",
    "login.again": "Send again",
    "login.err.link": "Invalid link. Please try signing in again.",
    "login.err.expirado": "This link expired or was already used. Request a new one.",
    "login.err.conta": "We couldn't find your account. Contact your administrator.",
    "login.home": "← Back to home",
    "topbar.home": "Home",
    "topbar.host": "Host",
    "topbar.kit": "Kit",
    "topbar.logout": "Sign out",
    "nav.library": "Library",
    "nav.planner": "Agenda",
    "library.title": "Facilitation methods",
    "library.search": "Search methods…",
    "library.allModes": "All formats",
    "library.add": "+ Agenda",
    "library.sort": "Sort",
    "library.saveSet": "Save filters",
    "library.saveSetPrompt": "Name for this filter set:",
    "library.deleteSet": "Delete set",
    "library.count": "methods",
    "library.countOne": "method",
    "sort.title": "Title (A–Z)",
    "sort.duration-asc": "Shortest first",
    "sort.duration-desc": "Longest first",
    "sort.group-asc": "Smallest groups first",
    "sort.group-desc": "Largest groups first",
    "detail.related": "Related methods",
    "library.empty": "No methods found.",
    "kit.loading": "Loading…",
    "kit.error": "We couldn't load the library right now.",
    "kit.retry": "Try again",
    "library.onlyFavourites": "Favourites only",
    "detail.favourite": "Add to favourites",
    "detail.unfavourite": "Remove from favourites",
    "mode.online": "Online",
    "mode.presencial": "In person",
    "mode.hibrido": "Hybrid",
    "detail.materials": "Materials",
    "detail.source": "Source",
    "detail.back": "← Library",
    "detail.add": "Add to agenda",
    "planner.title": "Agenda planner",
    "planner.empty": "Add methods from the library to build your agenda.",
    "planner.start": "Start",
    "planner.total": "Total time",
    "planner.export": "Export",
    "planner.exportCsv": "CSV",
    "planner.exportDocx": "Word (.docx)",
    "planner.docxMethod": "Method",
    "planner.docxFacOnly": "facilitator only",
    "planner.printPdf": "Print / PDF",
    "planner.csvOrder": "No.",
    "planner.csvBlock": "Block",
    "planner.csvDuration": "Duration",
    "planner.print": "Print",
    "planner.remove": "Remove",
    "planner.min": "min",
    "planner.clock.title": "Delivery clock",
    "planner.clock.start": "Start delivery",
    "planner.clock.next": "Next block",
    "planner.clock.stop": "End delivery",
    "planner.clock.onAir": "On air: {block}",
    "planner.clock.remaining": "{n} min left",
    "planner.clock.blockOver": "Block over by {n} min",
    "planner.clock.agendaOver": "Agenda over by {n} min",
    "planner.clock.agendaAhead": "Agenda ahead by {n} min",
    "planner.clock.onTime": "Agenda on time",
    "planner.clock.done": "Agenda finished",
    "planner.clock.live": "Delivery clock",
    "planner.dragHint": "Drag to reorder — times recompute automatically.",
    "planner.save": "Save",
    "planner.saveNew": "Save to my account",
    "planner.savedOk": "Agenda saved to your account.",
    "planner.saveFailed": "Couldn't save right now — your agenda is still in this browser.",
    "planner.saveRefused": "The server refused this save ({code}) — your agenda is still in this browser.",
    "planner.localOnly": "This agenda lives in this browser only. Sign in to save it.",
    "planner.linked": "Saved to your account. Changes reach the server when you save.",
    "planner.unsaved": "Draft in this browser — not saved to your account yet.",
    "planner.myAgendas": "My agendas",
    "planner.open": "Open",
    "planner.delete": "Delete",
    "planner.duplicate": "Duplicate",
    "planner.share": "Share",
    "planner.unshare": "Stop sharing",
    "planner.shareLink": "Read-only link",
    "planner.shareHint": "Anyone with the link can view this agenda without signing in. Stop sharing to disable it.",
    "planner.sharedBadge": "shared",
    "planner.facOnly": "Facilitator only",
    "planner.facOnlyHint": "Marked: this block stays out of the shared link and out of what a visitor prints or downloads. Applies after you save.",
    "planner.shareConfirm": "Share \"{title}\"? {hidden} of {total} block(s) are marked \"facilitator only\" and stay out of the link. The other {visible} are visible to anyone with the link, without signing in. Confirming saves the agenda, and the link serves that version. The other blocks keep their times, so the gap shows.",
    "planner.shareConfirmNone": "Share \"{title}\"? No block is marked \"facilitator only\": anyone with the link sees all {total} block(s). Confirming saves the agenda, and the link serves that version. The other blocks keep their times, so the gap shows.",
    "share.print": "Print / PDF",
    "share.csv": "CSV",
    "share.docx": "Word (.docx)",
    // live session in the planner + per-block "run now" (US-MB-6, W12 slice 1)
    "live.title": "Live session",
    "live.signedOut": "Running a block live needs you signed in. The planner keeps working in this browser.",
    "live.create": "Create session",
    "live.codeLabel": "or join with the code",
    "live.attach": "Use this code",
    "live.detach": "Detach the room",
    "live.joinCode": "Room code",
    "live.joinLink": "Link for participants",
    "live.projection": "Open projection",
    "live.run": "Run now",
    "live.noSpec": "This card doesn't carry a live round yet.",
    "live.noSession": "Create or enter a room to run this block.",
    "live.onAir": "on air — you close it when you like",
    "live.onAirTimed": "on air · {min} min",
    "live.badCode": "Invalid code. It's 6 characters, as shown on the projection.",
    "live.roomNotFound": "We couldn't find that room open. Check the code, or create a room.",
    "live.refused": "The round didn't open (code {code}). Nothing was sent to the room.",
    "live.close": "Close round",
    "live.closedRound": "closed",
    "live.superseded": "replaced by the next round — the room holds one open round at a time, which was true before this screen",
    "live.unknownOutcome": "We don't know what the room is showing: the request was not confirmed. Check the projection before clicking again.",
    "live.notHost": "Only a facilitator role (host or owner) can open a live room.",
    "live.createFailed": "Couldn't create the room (code {code}). No room was opened.",
    "live.attachFailed": "Couldn't use that code (code {code}). Nothing changed in your agenda.",
    "live.closeFailed": "Couldn't close the round (code {code}). It may still be open in the room.",
    "live.results": "Results",
    "live.resultsFailed": "Couldn't read the results (code {code}).",
    "live.noRounds": "Results appear when the round closes.",
    "live.notRecorded": "A draw is not recorded: the room keeps round results, not draws.",
    "live.hiddenTally": "Tally hidden during the round · responses",
    "live.armed": "this one is next — run it when you like",
    "share.readOnly": "Shared agenda — read only.",
    "share.gone": "Link unavailable",
    "share.goneHint": "This link doesn't exist, or the person who created the agenda turned it off.",
  },
  "es-LATAM": {
    // results dashboard (durable per-round metrics)
    "results.title": "Resultados de la sesión",
    "results.back": "Volver",
    "results.loading": "Cargando resultados…",
    "results.empty": "Todavía no hay rondas cerradas. Los resultados aparecen aquí en cuanto cierres la primera pregunta.",
    "results.unavailable": "No se pudieron cargar los resultados ahora. Inténtalo en un momento.",
    "results.notFound": "Sesión no encontrada.",
    "results.rounds": "rondas",
    "results.totalResponses": "respuestas en total",
    "results.peakResponses": "mayor participación en una ronda",
    "results.peakHint": "Mayor número de respuestas en una sola ronda — no es el conteo de personas en la sala.",
    "results.responses": "respuestas",
    "results.responseOne": "respuesta",
    "results.scored": "Con puntaje",
    "results.round": "Ronda",
    "results.export": "Descargar CSV",
    "results.exportPdf": "Descargar informe en PDF",
    "results.exportLocked": "La exportación está disponible en los planes de pago",
    "results.exportLockedHint": "Este resultado se generó en un plan gratuito. La exportación sigue bloqueada aunque te suscribas — a quienes participaron en esa sala se les informó así.",
    "results.withheld": "{n} ronda(s) fuera del período de retención de tu plan ({days} días).",
    "results.withheldHint": "Los planes de pago guardan por más tiempo.",
    "results.anonymous": "Autoría oculta",
    "results.report": "Imprimir esta página",
    "results.reportHeader": "Klapzo · Informe de la sesión {id} · generado el {date}",
    // planes + upgrade (adonde llega todo 402)
    "plan.free": "Gratuito",
    "plan.facilitador": "Facilitador",
    "plan.equipe": "Equipo",
    "plan.evento": "Evento",
    "plan.corporativo": "Corporativo",
    "upgrade.title": "Planes Klapzo",
    "upgrade.loading": "Cargando planes…",
    "upgrade.unavailable": "No se pudieron cargar los planes ahora. Intenta de nuevo en unos instantes.",
    "upgrade.back": "Volver",
    "upgrade.current": "Plan actual",
    "upgrade.currentPlan": "Tu plan",
    "upgrade.buyMonthly": "Suscribirse por {money}/mes",
    "upgrade.buyAnnual": "Suscribirse por {money}/año",
    "upgrade.buyOneOff": "Comprar por {money} — {days} días",
    "upgrade.redirecting": "Redirigiendo…",
    "upgrade.emailPrompt": "Correo para el cobro:",
    "upgrade.emailInvalid": "Ingresa un correo válido para continuar.",
    "upgrade.checkoutFailed": "No se pudo iniciar el pago. Intenta de nuevo en unos instantes.",
    "upgrade.pastDue": "Pago pendiente.",
    "upgrade.pastDueHint": "Tu suscripción {plan} está en mora, así que las salas volvieron a los límites del plan gratuito. Regulariza para restaurar el acceso.",
    "upgrade.passActive": "Pase {plan} activo hasta {date}.",
    "upgrade.talkToSales": "Hablar con ventas",
    "upgrade.taxNote": "Precios en reales brasileños, pagaderos por PIX, boleto o tarjeta vía Mercado Pago.",
    "upgrade.corporativoBlurb": "Desde R$ 890/mes. Salas de hasta 1.000 personas, 25 asientos, retención indefinida, SSO y marca propia.",
    "upgrade.blurb.facilitador": "Rondas ilimitadas, hasta 100 participantes por sala, exportación CSV/JSON y el Klapzo Kit completo.",
    "upgrade.blurb.equipe": "Todo lo de Facilitador, con 5 asientos, 5 salas simultáneas y 200 participantes por sala.",
    "upgrade.blurb.evento": "Pase suelto de 7 días para una conferencia: 300 participantes por sala, sin suscripción.",
    "limit.roundsTitle": "Límite de rondas alcanzado",
    "limit.roundsBody": "El plan {plan} permite {limit} rondas por sala. Haz upgrade para abrir rondas ilimitadas.",
    "limit.sessionsTitle": "Límite de salas simultáneas alcanzado",
    "limit.sessionsBody": "El plan {plan} permite {limit} sala(s) a la vez. Cierra una sala o haz upgrade.",
    "limit.asyncTitle": "Sesión de larga duración no disponible",
    "limit.asyncBody": "Las sesiones de larga duración están disponibles desde el plan Facilitador. En tu plan una sala dura {limit} horas.",
    "limit.cta": "Ver planes",
    "sessionOver.title": "Esta sala fue cerrada",
    "sessionOver.closed": "Cerraste esta sala. Crea una nueva para continuar.",
    "sessionOver.expired": "Una sala dura como máximo 12 horas y esta llegó al límite. Crea una nueva para continuar — sus resultados siguen disponibles.",
    "results.votes": "votos",

    // promptvote (US-LV-10)
    "pv.title": "Ronda de respuestas",
    "pv.write": "Escribe tu respuesta",
    "pv.writeHint": "Una sola línea. Tu respuesta aparecerá en la pantalla del grupo — sin datos personales.",
    "pv.sent": "¡Respuesta enviada! Sigue la votación.",
    "pv.waitingWrite": "El grupo está escribiendo…",
    "pv.submissions": "respuestas recibidas",
    "pv.submissionOne": "respuesta recibida",
    "pv.vote": "Vota por la mejor",
    "pv.voteHint": "No puedes votar por tu propia respuesta.",
    "pv.voted": "¡Voto registrado!",
    "pv.votesCast": "votos registrados",
    "pv.reveal": "Resultados",
    "pv.votes": "votos",
    "pv.voteOne": "voto",
    // urna honesta (W10a)
    "pv.omitted": "{n} frases quedaron fuera del sorteo de la urna.",
    "pv.omittedOne": "1 frase quedó fuera del sorteo de la urna.",
    // entrar desde la pantalla (W10a)
    "join.instruction": "Apunta la cámara o escribe el código:",
    "join.qrLabel": "Código QR para entrar a la sala con el código {code}",
    "projection.more": "+{n} no están en pantalla",
    "projection.moreOne": "+1 no está en pantalla",
    // picker / "Sorteio" (US-LV-6, W5). El nombre del producto es "Sorteio" en los tres idiomas.
    "picker.title": "Sorteio",
    "picker.open": "Sorteio",
    "picker.one": "Sortear una persona",
    "picker.weighted": "Sortear a quien participó menos",
    "picker.teams": "Sortear equipos",
    "picker.teamCount": "N.º de equipos",
    "picker.rotation": "Orden de rotación",
    "picker.pool": "o sortea de esta lista (uno por línea)",
    "picker.poolPlaceholder": "Un nombre por línea",
    "picker.spinHint": "El sorteo se hace en el servidor al abrir la ronda. Nadie — ni el facilitador — ve el resultado antes de que la ronda cierre.",
    "picker.spinning": "Sorteando…",
    "picker.drawn": "Sorteado",
    "picker.nobody": "Nadie para sortear.",
    "picker.you": "¡Fuiste tú!",
    "picker.yourTeam": "Tu equipo: {n}",
    "picker.yourPosition": "Eres el n.º {n}",
    "picker.team": "Equipo",
    "picker.candidates": "nombres en el sorteo",
    "picker.candidateOne": "nombre en el sorteo",
    "picker.teamsWord": "equipos",
    "picker.teamOne": "equipo",
    "picker.startsWith": "empieza",
    "picker.badTeams": "Nº de equipos: un número entero de 2 a 20.",
    "picker.badPool": "Lista: hasta 100 nombres, cada uno de hasta 48 caracteres.",
    "picker.failed": "No se pudo abrir el sorteo ({code}). Revisa la lista: nombres con teléfono o documento se rechazan.",
    // Objetivo (W6a, US-LV-13): quién recibe una versión distinta de la ronda.
    "target.title": "Objetivo",
    "target.hint": "Marca quién recibe un brief privado en la próxima encuesta, quiz o pregunta abierta. La sala no ve a quién elegiste. No aplica a los borradores de IA ni a la carrera.",
    "target.empty": "Todavía no hay nadie en la sala.",
    "target.brief": "Brief privado",
    "target.briefPlaceholder": "Solo quienes marques van a leer esto.",
    "target.count": "{n} personas reciben una versión privada",
    "target.countOne": "1 persona recibe una versión privada",
    "target.tooLong": "El brief privado admite 500 caracteres como máximo.",
    "target.rosterError": "No se pudo cargar quién está en la sala. Vuelve a entrar si tu sesión expiró.",
    "target.private": "Solo tú ves esto",
    "roster.away": "fuera de la sala",
    // equipos + tablero por equipo + quién responde (W7, US-LV-14)
    "teams.title": "Equipos",
    "teams.hint": "Divide la sala en equipos y mira el tablero por equipo junto al individual. Cada persona solo conoce su propio equipo — la sala nunca ve quién es de quién.",
    "teams.count": "Cuántos equipos",
    "teams.draw": "Sortear equipos",
    "teams.defaultName": "Equipo {n}",
    "teams.name": "Nombre del equipo {n}",
    "teams.secret": "Miembros en secreto",
    "teams.secretHint": "Esto cambia solo tu pantalla: la sala nunca recibe la lista de ningún equipo, marcada o no.",
    "teams.aggregate": "Puntuación del equipo",
    "teams.sum": "Suma de puntos",
    "teams.mean": "Promedio de puntos (nadie carga al equipo solo)",
    "teams.save": "Guardar equipos",
    "teams.clear": "Quitar equipos",
    "teams.saved": "Equipos guardados.",
    "teams.empty": "Todavía no hay nadie en la sala.",
    "teams.failed": "No se pudieron guardar los equipos ({code}).",
    "teams.standings": "Tablero por equipo",
    "teams.size": "{n} personas",
    "teams.sizeOne": "1 persona",
    "teams.leader": "Equipo al frente",
    "teams.yourTeam": "Tu equipo: {name}",
    "teams.myBoard": "Eres el nº {r} de {n} en tu equipo · equipo nº {tr} de {tc}",
    "teams.floorTitle": "Quién responde",
    "teams.floorHint": "Vale para encuesta, quiz, nube de palabras, preguntas (Q&A) y ronda de frases. Nunca va en sorteo, borrador de IA ni carrera, y se limpia al enviar la ronda. Quien queda fuera puede seguir apoyando (▲) las preguntas.",
    "teams.floorAll": "Responde todo el mundo",
    "teams.floorCount": "{n} personas pueden responder",
    "teams.floorCountOne": "1 persona puede responder",
    "teams.readOnly": "Solo quien fue elegido responde esta ronda",
    "teams.membersHidden": "miembros ocultos",
    "teams.noMembers": "todavía nadie",
    // final / segunda vuelta (W8a, US-LV-15): el resultado de una ronda pasa a ser el contenido de la próxima.
    "derive.title": "Final",
    "derive.hint": "Abre una ronda nueva con las respuestas más votadas de esta. Nadie escribe nada: el resultado de esta ronda pasa a ser las opciones de la próxima.",
    "derive.k": "Cuántas opciones",
    "derive.prompt": "Pregunta de la final",
    "derive.time": "Tiempo",
    "derive.timeOff": "Sin tiempo",
    "derive.timeSeconds": "{n}s",
    "derive.open": "Abrir la final",
    "derive.promptRunoff": "Segunda vuelta: ¿cuál queda?",
    "derive.promptFinal": "Final: ¿cuál es la mejor?",
    "derive.promptWords": "¿Qué palabra lo representa mejor?",
    "derive.failed": "No se pudo abrir la final ({code}).",
    "derive.e.SOURCE_NOT_CLOSED": "cierra la ronda primero",
    "derive.e.CANNOT_DERIVE_FROM_TYPE": "un sorteo no tiene respuestas para llevar",
    "derive.e.CANNOT_DERIVE_FROM_MEAN": "las opciones de una escala son notas, no candidatas",
    "derive.e.NOT_ENOUGH_CANDIDATES": "menos de dos respuestas para elegir",
    // priorización (W9a, US-LV-16): votación por puntos, presupuesto fijo y ranking — payloads de mc.
    "prio.title": "Priorización",
    "prio.hint": "La sala reparte puntos entre las opciones o las ordena. Es una encuesta: las mismas barras, sin un tipo nuevo de actividad.",
    "prio.prompt": "Pregunta",
    "prio.presetPrompt": "¿En qué invertimos primero?",
    "prio.options": "Opciones (una por línea)",
    "prio.optionsPlaceholder": "Onboarding\nReportes\nIntegraciones",
    "prio.mode": "Modo",
    "prio.modePoints": "Puntos",
    "prio.modeRank": "Ranking",
    "prio.budget": "Presupuesto",
    "prio.budgetPoints": "{n} puntos",
    "prio.budgetFixed": "Presupuesto fijo (100)",
    "prio.open": "Abrir priorización",
    "prio.failed": "No se pudo abrir la priorización ({code}).",
    "prio.badPrompt": "Escribe la pregunta de la ronda.",
    "prio.badOptions": "Entre 2 y 10 opciones, una por línea, de hasta 120 caracteres cada una.",
    "prio.e.PAYLOAD_MAX_OPTIONS": "máximo 10 opciones — un celular no ordena catorce cosas",
    "prio.e.INVALID_BUDGET": "el presupuesto es un número entero de 1 a 100",
    "prio.e.PAYLOAD_UNSCORED": "una priorización no da puntos: no hay respuesta correcta",
    "prio.e.VARIANT_OPTIONS_ON_PAYLOAD": "el objetivo puede cambiar el enunciado, no las opciones",
    "prio.unit": "puntos",
    "prio.remaining": "quedan {n} de {b} puntos",
    "prio.less": "menos {label}, {n} puntos",
    "prio.lessOne": "menos {label}, 1 punto",
    "prio.more": "más {label}, {n} puntos",
    "prio.moreOne": "más {label}, 1 punto",
    "prio.moved": "{label}: posición {i} de {n}",
    "prio.up": "subir {label}",
    "prio.down": "bajar {label}",
    // escala con promedio (W9b, US-LV-5): las opciones son los puntos y el resultado es el PROMEDIO.
    "scale.title": "Escala",
    "scale.hint": "Las opciones son los puntos de la escala y el resultado es su promedio: la encuesta de siempre, con el promedio al lado de las barras.",
    "scale.prompt": "Pregunta",
    "scale.presetPrompt": "De 1 a 5, ¿cómo llegas hoy?",
    "scale.from": "De",
    "scale.to": "hasta",
    "scale.points": "{n} puntos",
    "scale.open": "Abrir escala",
    "scale.mean": "promedio {v}",
    "scale.meanLabel": "promedio",
    "scale.failed": "No se pudo abrir la escala ({code}).",
    "scale.badPrompt": "Escribe la pregunta de la ronda.",
    "scale.badRange": "La escala va de 2 a 11 puntos y empieza por debajo de su fin.",
    "hosted.title": "Pantalla única (sin celular)",
    "hosted.hint": "El grupo decide en conjunto mirando la proyección y tú registras la elección con un toque. Nadie necesita celular ni red, y no se crea ningún dato de participantes.",
    "hosted.prompt": "Pregunta",
    "hosted.options": "Opciones (una por línea, de 2 a 6)",
    "hosted.open": "Abrir en pantalla única",
    "hosted.failed": "No se pudo abrir la ronda en pantalla única ({code}).",
    "hosted.badPrompt": "Escribe la pregunta de la ronda.",
    "hosted.badOptions": "Indica de 2 a 6 opciones distintas, una por línea.",
    "hosted.recordTitle": "¿El grupo decidió? Registra la elección",
    "hosted.record": "Registrar: {label}",
    "hosted.noChoice": "Cerrar sin elección",
    "hosted.recordFailed": "No se pudo registrar la elección ({code}). La ronda sigue abierta.",
    "hosted.caption": "Pantalla única: el grupo decide en conjunto y el anfitrión registra la elección.",
    "hosted.recorded": "Elección del grupo, registrada por el anfitrión",
    "hosted.badge": "Ronda del anfitrión · pantalla única, sin participantes",
    "hosted.phone": "Esta ronda ocurre en la pantalla: el grupo decide en conjunto.",
    "hosted.e.INVALID_HOSTED_CHOICE": "esa opción no existe o la ronda no es de pantalla única",
    "hosted.e.HOSTED_NEEDS_PLAIN_MC": "la pantalla única no admite puntaje, prioridad, promedio ni resultado oculto",
    "hosted.e.HOSTED_IS_UNTIMED": "la pantalla única no tiene cronómetro: la cierra el anfitrión",
    "hosted.e.HOSTED_NOT_TARGETED": "la pantalla única no tiene destinatarios ni lista de elegibles",
    "hosted.e.INVALID_HOSTED": "la pantalla única solo existe en una encuesta de opción múltiple",
    "scale.e.AGGREGATE_ONLY_ON_MC": "solo una encuesta tiene promedio: la nube, el muro, la urna y el sorteo no",
    "scale.e.INVALID_AGGREGATE": "el promedio es el único agregado además de la suma",
    "scale.e.MEAN_NEEDS_VALUES": "cada opción necesita el punto de la escala que representa",
    "scale.e.VARIANT_OPTIONS_ON_MEAN": "el objetivo puede cambiar el enunciado, no la escala",
    "scale.e.MEAN_UNSCORED": "una escala no da puntos: un promedio no tiene respuesta correcta",
    "scale.e.MEAN_NEEDS_ONE": "por ahora el promedio es de un asunto por ronda, con un solo toque",
    "picker.weightedRoomOnly": "Solo para quienes están en la sala; con una lista, el sorteo es uniforme.",

    "mc.answered": "respondieron",
    "mc.answeredOne": "respondió",
    "mc.tallyHidden": "el resultado se muestra al cerrar la ronda",
    "mc.viewLabel": "Ver resultado como",
    "mc.viewBars": "Barras",
    "mc.viewDonut": "Anillo",
    "error.MODERATION_REFUSED":
      "Esta moderación no se aplicó: el límite de una respuesta por persona impide devolver esta pregunta a la pantalla, o ya no está en esta ronda.",
    "review.title": "En revisión",
    "review.hint": "Nada de esto está en la pantalla ni en los celulares. Apruébalo para publicar.",
    "review.approve": "Aprobar",
    "review.dismiss": "Descartar",
    "grp.button": "Agrupar con IA",
    "grp.working": "Agrupando…",
    "grp.title": "Categorías (solo tú las ves)",
    "grp.hint": "La IA lee las respuestas visibles, sin nombres, y propone categorías. Nada va a la pantalla ni a los celulares.",
    "grp.skipped": "{n} respuesta(s) no se enviaron a la IA (vacías o con aspecto de dato personal).",
    "error.GROUPING_FAILED": "No se pudo agrupar ahora. La ronda sigue como estaba. Inténtalo de nuevo en un momento.",
    "error.NOTHING_TO_GROUP": "Aún no hay suficientes respuestas para agrupar (mínimo dos).",
    "error.NOT_AN_OPEN_ROUND": "La agrupación por IA solo vale para rondas de preguntas abiertas.",
    "error.AI_NOT_CONFIGURED": "La agrupación por IA no está disponible en este entorno.",
    "a11y.liveResults": "Resultados en vivo",
    "a11y.words": "palabras",
    "a11y.wordOne": "palabra",
    "a11y.questions": "preguntas",
    "a11y.questionOne": "pregunta",
    "a11y.mostCited": "Más citada",
    "a11y.leader": "Líder",
    "a11y.correct": "respuesta correcta",
    "a11y.winner": "más votada",
    "a11y.highlighted": "destacada por el facilitador",
    "a11y.inReview": "en revisión",
    "pv.hide": "Ocultar",
    "pv.open": "Ronda de respuestas",
    "pv.presetPrompt": "¿La peor excusa para faltar a una reunión?",
    "pv.err.EMPTY": "Escribe algo antes de enviar.",
    "pv.err.LOOKS_LIKE_PII": "Parece un documento o teléfono — no envíes datos personales.",
    "pv.err.SELF_VOTE": "No puedes votar por tu propia respuesta.",
    "pv.err.ALREADY_VOTED": "Ya votaste en esta ronda.",
    "pv.err.NOT_ON_BALLOT": "Esa respuesta ya no está disponible.",
    "pv.err.WRONG_PHASE": "Se acabó el tiempo de esta etapa.",
    "pv.err.NOT_ELIGIBLE": "Esta ronda no está aceptando tu respuesta.",
    "pv.err.fallback": "No se pudo enviar. Inténtalo de nuevo.",
    "pv.err.RATE_LIMITED": "¡Más despacio! Intenta de nuevo en un momento.",
    "join.title": "Entrar a la sesión",
    "join.nickname": "Tu apodo",
    "join.cta": "Entrar",
    "join.seatExpired": "Tu sesión expiró. Entra de nuevo.",
    "join.longRunningRoom": "Esta sala queda abierta varios días — tu lugar, tu equipo y tus puntos se guardan EN ESTE navegador hasta {date}, o hasta que salgas de la sala.",
    "join.longRunningRoomNoDate": "Esta sala queda abierta varios días — tu lugar, tu equipo y tus puntos se guardan EN ESTE navegador hasta que la sesión se cierre, o hasta que salgas de la sala.",
    "join.invalidCode": "Código inválido.",
    "join.notFound": "Sesión no encontrada o cerrada.",
    "join.rateLimited": "Demasiados intentos. Espera un momento.",
    "join.busyRetrying": "Mucha gente entrando ahora; reintentando en unos segundos.",
    "join.nicknameRequired": "Escribe un apodo.",
    "join.nicknamePii": "Ese apodo parece un CPF o teléfono — usa un nombre.",
    "participant.waiting": "Esperando que el facilitador inicie…",
    "participant.submitted": "¡Respuesta enviada!",
    "participant.typeWord": "Escribe una palabra",
    "participant.typeAnswer": "Escribe tu respuesta",
    "participant.send": "Enviar",
    "wc.quota": "Quedan {n} de {max} palabras.",
    "panel.namedNotice": "En esta ronda tu apodo aparece junto a tu frase, para toda la sala.",
    "participant.queued": "{n} respuestas se enviarán cuando vuelva la conexión.",
    "participant.queuedOne": "1 respuesta se enviará cuando vuelva la conexión.",
    "participant.queueFull": "Ya hay {n} respuestas esperando en esta ronda. Espera la conexión para enviar más.",
    "participant.unsentClosed": "La ronda cerró antes de que volviera la conexión.",
    "participant.leaveRoom": "Salir de esta sala",
    "round.openUntil": "Abierta hasta {date}",
    "round.answeredComeBack": "Respuesta enviada. Vuelve por este mismo enlace cuando el facilitador abra la siguiente.",
    "participant.roomFull": "La sala está llena por ahora. Seguimos intentando entrar; quédate en esta pantalla.",
    "participant.offlineHint": "Sin conexión con la sala — prueba con los datos móviles.",
    "presenter.title": "Panel del facilitador",
    "presenter.create": "Crear sesión",
    "presenter.longRunning": "Sesión de larga duración (días)",
    "presenter.longRunningHint": "La sala queda en pie por semanas: el lugar de cada persona y el puntaje atraviesan los días.",
    "series.title": "Tendencia de la serie {id}",
    "series.loading": "Cargando la tendencia…",
    "series.unavailable": "No se pudo cargar la tendencia ahora. Inténtalo de nuevo en unos instantes.",
    "series.empty": "Aún no hay puntos. La línea aparece cuando dos sesiones de la serie cierran la misma pregunta de escala con 5 o más respondientes.",
    "series.needTwo": "Solo hay un punto: falta otra sesión de la serie con la misma pregunta.",
    "series.chartLabel": "Promedio por sesión: {prompt}",
    "series.col.date": "Cerrada el",
    "series.col.mean": "Promedio",
    "series.col.respondents": "Respondientes",
    "series.withheld": "{n} punto(s) oculto(s): sesión con menos de 5 respondientes.",
    "series.minRespondents": "Solo los promedios cruzan sesiones, nunca las respuestas de las personas. La pregunta es la misma cuando el texto coincide, sin contar mayúsculas, acentos ni puntuación.",
    "series.link": "Ver la tendencia de la serie",
    "presenter.series": "Serie (opcional)",
    "presenter.seriesHint": "Usa el mismo nombre en cada encuentro para ver el promedio de la misma pregunta de escala en el tiempo. Letras, números, guion y guion bajo, hasta 64.",
    "presenter.seriesInvalid": "Usa solo letras sin acento, números, guion y guion bajo (hasta 64). Si no, la sesión se crea sin serie.",
    "presenter.horizon": "Enlace válido hasta {date}",
    "presenter.attendance": "{answered} de {enrolled} respondieron",
    "presenter.roundWindow": "Cierra en",
    "presenter.windowOff": "Yo la cierro",
    "presenter.windowHours": "{n} h",
    "presenter.windowDays": "{n} días",
    "presenter.roundOptions": "Opciones de la próxima ronda",
    "presenter.wordsPerPerson": "Palabras por persona",
    "presenter.wordsUnlimited": "Sin límite",
    "presenter.showLive": "Mostrar en vivo",
    "presenter.oneEntryPerPerson": "Una respuesta por persona",
    "presenter.namedPanel": "Panel con nombre",
    "presenter.namedPanelHint": "El apodo de quien escribió aparece junto a la frase y toda la sala lo ve. El celular lo avisa antes de enviar.",
    "presenter.showLiveTypes": "Vale para encuestas, nube de palabras y preguntas. La votación de ideas y el sorteo tienen su propio ritmo.",
    "error.ROUND_TOO_LONG": "Esta sesión solo acepta rondas de hasta 10 minutos.",
    "error.ROUND_OUTLIVES_SESSION": "La ronda cerraría después del fin de la sesión. Acórtala o crea una sesión más larga.",
    "error.ASYNC_NEEDS_FLAT_SCORING": "En una ronda de varios días, la puntuación por velocidad premia a quien abrió el correo primero. Usa puntuación por acierto.",
    "error.INVALID_MAX_WORDS": "El límite de palabras por persona solo existe en la nube de palabras, y va de 1 a 3.",
    "error.INVALID_PANEL": "Una entrada por persona y el panel con nombre solo existen en preguntas y respuestas.",
    "error.INVALID_REVEAL_TALLY": "\"Mostrar en vivo\" vale para encuestas, nube de palabras y preguntas — no para la votación de ideas ni el sorteo.",
    "presenter.code": "Código de acceso",
    "presenter.participants": "Participantes",
    "presenter.open": "Abrir",
    "presenter.close": "Cerrar pregunta",
    "presenter.reveal": "Revelar",
    "presenter.raceStart": "Iniciar carrera de preguntas",
    "presenter.raceNext": "Siguiente ronda",
    "presenter.raceFinish": "Finalizar y mostrar el podio",
    "presenter.raceRound": "Ronda",
    "presenter.raceEnded": "Carrera finalizada",
    "presenter.raceHint": "Varias preguntas cronometradas en secuencia, con clasificación acumulada.",
    "presenter.racePreset1": "¿Cuánto es 1 + 1?",
    "presenter.racePreset2": "¿Cuál es la capital de Brasil?",
    "presenter.racePreset3": "¿Cuántos días tiene una semana?",
    "presenter.poll": "Encuesta",
    "presenter.quiz": "Quiz puntuado",
    "presenter.scoring": "Puntuación del quiz",
    "presenter.scoringAccuracy": "Precisión — puntos fijos por acierto",
    "presenter.scoringSpeed": "Velocidad — acertar rápido vale más",
    "presenter.scoringSelfPaced": "Sin cronómetro — cada uno a su ritmo",
    "presenter.wordcloud": "Nube de palabras",
    "presenter.qa": "Preguntas y respuestas",
    "presenter.projection": "Abrir proyección",
    "presenter.aiTitle": "Generar quiz con IA (borrador)",
    "presenter.aiTopic": "Tema del quiz",
    "presenter.aiGenerate": "Generar borradores",
    "presenter.aiOpen": "Abrir",
    "presenter.aiHint": "La IA propone; tú revisas y abres. Nada se publica automáticamente.",
    "presenter.devLogin": "Login dev rápido",
    "projection.joinPrompt": "Entra en {url} con el código",
    "projection.waiting": "Esperando la primera pregunta…",
    "common.leaderboard": "Clasificación",
    "common.votes": "votos",
    "common.responses": "respuestas",
    "common.closed": "Pregunta cerrada",
    "login.title": "Inicia sesión en Klapzo",
    "login.subtitle": "Te enviaremos un enlace de acceso por correo. Sin contraseña.",
    "login.email": "Tu correo",
    "login.cta": "Enviar enlace de acceso",
    "login.sent": "¡Enlace enviado!",
    "login.sentDesc": "Si existe una cuenta con ese correo, el enlace de acceso ya está en la bandeja de entrada. Válido por 30 minutos.",
    "login.again": "Enviar de nuevo",
    "login.err.link": "Enlace inválido. Intenta iniciar sesión otra vez.",
    "login.err.expirado": "Este enlace expiró o ya se usó. Pide uno nuevo.",
    "login.err.conta": "No encontramos tu cuenta. Contacta al administrador.",
    "login.home": "← Volver al inicio",
    "topbar.home": "Inicio",
    "topbar.host": "Facilitar",
    "topbar.kit": "Kit",
    "topbar.logout": "Salir",
    "nav.library": "Biblioteca",
    "nav.planner": "Agenda",
    "library.title": "Métodos de facilitación",
    "library.search": "Buscar métodos…",
    "library.allModes": "Todos los formatos",
    "library.add": "+ Agenda",
    "library.sort": "Ordenar",
    "library.saveSet": "Guardar filtros",
    "library.saveSetPrompt": "Nombre para este conjunto de filtros:",
    "library.deleteSet": "Eliminar conjunto",
    "library.count": "métodos",
    "library.countOne": "método",
    "sort.title": "Título (A–Z)",
    "sort.duration-asc": "Más cortos primero",
    "sort.duration-desc": "Más largos primero",
    "sort.group-asc": "Grupos pequeños primero",
    "sort.group-desc": "Grupos grandes primero",
    "detail.related": "Métodos relacionados",
    "library.empty": "No se encontraron métodos.",
    "kit.loading": "Cargando…",
    "kit.error": "No pudimos cargar la biblioteca en este momento.",
    "kit.retry": "Intentar de nuevo",
    "library.onlyFavourites": "Solo favoritos",
    "detail.favourite": "Agregar a favoritos",
    "detail.unfavourite": "Quitar de favoritos",
    "mode.online": "En línea",
    "mode.presencial": "Presencial",
    "mode.hibrido": "Híbrido",
    "detail.materials": "Materiales",
    "detail.source": "Fuente",
    "detail.back": "← Biblioteca",
    "detail.add": "Agregar a la agenda",
    "planner.title": "Planificador de agenda",
    "planner.empty": "Agrega métodos de la biblioteca para armar tu agenda.",
    "planner.start": "Inicio",
    "planner.total": "Duración total",
    "planner.export": "Exportar",
    "planner.exportCsv": "CSV",
    "planner.exportDocx": "Word (.docx)",
    "planner.docxMethod": "Método",
    "planner.docxFacOnly": "solo facilitador",
    "planner.printPdf": "Imprimir / PDF",
    "planner.csvOrder": "N.º",
    "planner.csvBlock": "Bloque",
    "planner.csvDuration": "Duración",
    "planner.print": "Imprimir",
    "planner.remove": "Quitar",
    "planner.min": "min",
    "planner.clock.title": "Reloj de entrega",
    "planner.clock.start": "Iniciar entrega",
    "planner.clock.next": "Siguiente bloque",
    "planner.clock.stop": "Terminar entrega",
    "planner.clock.onAir": "En curso: {block}",
    "planner.clock.remaining": "{n} min restantes",
    "planner.clock.blockOver": "Bloque excedido en {n} min",
    "planner.clock.agendaOver": "Agenda excedida en {n} min",
    "planner.clock.agendaAhead": "Agenda adelantada en {n} min",
    "planner.clock.onTime": "Agenda a tiempo",
    "planner.clock.done": "Agenda terminada",
    "planner.clock.live": "Reloj de entrega",
    "planner.dragHint": "Arrastra para reordenar — los horarios se recalculan solos.",
    "planner.save": "Guardar",
    "planner.saveNew": "Guardar en mi cuenta",
    "planner.savedOk": "Agenda guardada en tu cuenta.",
    "planner.saveFailed": "No pudimos guardar ahora — tu agenda sigue en este navegador.",
    "planner.saveRefused": "El servidor rechazó este guardado ({code}) — tu agenda sigue en este navegador.",
    "planner.localOnly": "Esta agenda queda solo en este navegador. Inicia sesión para guardarla.",
    "planner.linked": "Guardada en tu cuenta. Los cambios llegan al servidor cuando guardas.",
    "planner.unsaved": "Borrador en este navegador — todavía sin guardar en tu cuenta.",
    "planner.myAgendas": "Mis agendas",
    "planner.open": "Abrir",
    "planner.delete": "Eliminar",
    "planner.duplicate": "Duplicar",
    "planner.share": "Compartir",
    "planner.unshare": "Dejar de compartir",
    "planner.shareLink": "Enlace de solo lectura",
    "planner.shareHint": "Quien tenga el enlace ve la agenda sin iniciar sesión. Deja de compartir para desactivarlo.",
    "planner.sharedBadge": "compartida",
    "planner.facOnly": "Solo facilitador",
    "planner.facOnlyHint": "Marcado: este bloque queda fuera del enlace compartido y de lo que el visitante imprime o descarga. Vale después de guardar.",
    "planner.shareConfirm": "¿Compartir \"{title}\"? {hidden} de {total} bloque(s) están como \"solo facilitador\" y quedan fuera del enlace. Los otros {visible} los ve quien tenga el enlace, sin iniciar sesión. Al confirmar, la agenda se guarda y el enlace sirve esa versión. Los horarios de los otros bloques no cambian, así que el hueco se ve.",
    "planner.shareConfirmNone": "¿Compartir \"{title}\"? Ningún bloque está como \"solo facilitador\": quien tenga el enlace ve los {total} bloque(s). Al confirmar, la agenda se guarda y el enlace sirve esa versión. Los horarios de los otros bloques no cambian, así que el hueco se ve.",
    "share.print": "Imprimir / PDF",
    "share.csv": "CSV",
    "share.docx": "Word (.docx)",
    // sesión en vivo en el planificador + "correr ahora" por bloque (US-MB-6, W12 tramo 1)
    "live.title": "Sesión en vivo",
    "live.signedOut": "Correr un bloque en vivo requiere iniciar sesión. El planificador sigue funcionando en este navegador.",
    "live.create": "Crear sesión",
    "live.codeLabel": "o entra con el código",
    "live.attach": "Usar este código",
    "live.detach": "Desconectar la sala",
    "live.joinCode": "Código de la sala",
    "live.joinLink": "Enlace para los participantes",
    "live.projection": "Abrir proyección",
    "live.run": "Correr ahora",
    "live.noSpec": "Esta ficha todavía no trae una ronda en vivo.",
    "live.noSession": "Crea o indica una sala para correr este bloque.",
    "live.onAir": "al aire — la cierras cuando quieras",
    "live.onAirTimed": "al aire · {min} min",
    "live.badCode": "Código inválido. Son 6 caracteres, como aparecen en la proyección.",
    "live.roomNotFound": "No encontramos esa sala abierta. Revisa el código o crea una sala.",
    "live.refused": "La ronda no abrió (código {code}). Nada se envió a la sala.",
    "live.close": "Cerrar ronda",
    "live.closedRound": "cerrada",
    "live.superseded": "reemplazada por la ronda siguiente — la sala mantiene una ronda abierta a la vez, y eso ya era así antes de esta pantalla",
    "live.unknownOutcome": "No sabemos qué está mostrando la sala: el envío no fue confirmado. Revisa la proyección antes de volver a hacer clic.",
    "live.notHost": "Solo un rol de facilitador (host u owner) puede abrir una sala en vivo.",
    "live.createFailed": "No pudimos crear la sala (código {code}). No se abrió ninguna sala.",
    "live.attachFailed": "No pudimos usar ese código (código {code}). Nada cambió en tu agenda.",
    "live.closeFailed": "No pudimos cerrar la ronda (código {code}). Puede seguir abierta en la sala.",
    "live.results": "Resultados",
    "live.resultsFailed": "No pudimos leer los resultados (código {code}).",
    "live.noRounds": "Los resultados aparecen cuando la ronda cierra.",
    "live.notRecorded": "Un sorteo no queda registrado: la sala guarda el resultado de las rondas, no de los sorteos.",
    "live.hiddenTally": "Conteo oculto durante la ronda · respuestas",
    "live.armed": "la siguiente es esta — córrela cuando quieras",
    "share.readOnly": "Agenda compartida — solo lectura.",
    "share.gone": "Enlace no disponible",
    "share.goneHint": "Este enlace no existe o quien creó la agenda lo desactivó.",
  },
};

function resolveLocale(): Locale {
  // Runs at module load, so it must survive having no browser at all: a plain `vitest` run has no
  // `location`, and the prerender pass will import this in Node too. Defaulting to pt-BR outside a
  // browser is both the correct fallback and the difference between a test file and a crash.
  if (typeof location === "undefined" || typeof localStorage === "undefined") return "pt-BR";
  const fromQuery = new URLSearchParams(location.search).get("lang");
  const stored = localStorage.getItem("klapzo.locale");
  const candidate = (fromQuery ?? stored ?? navigator.language) || "pt-BR";
  if (candidate.startsWith("en")) return "en-US";
  if (candidate.startsWith("es")) return "es-LATAM";
  return "pt-BR";
}

let current: Locale = resolveLocale();

export function getLocale(): Locale {
  return current;
}
export function setLocale(locale: Locale): void {
  current = locale;
  // Guarded for the same reason `resolveLocale` is: this module also runs under node — in vitest
  // and in the prerender pass — where `localStorage` does not exist. Switching the language is the
  // point; persisting the choice is a browser nicety, so only that part is skipped.
  if (typeof localStorage !== "undefined") localStorage.setItem("klapzo.locale", locale);
}

export function t(key: string, vars?: Record<string, string>): string {
  let s = STRINGS[current][key] ?? STRINGS["pt-BR"][key] ?? key;
  if (vars) for (const [k, v] of Object.entries(vars)) s = s.replace(`{${k}}`, v);
  return s;
}
